import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { sharedTableStyles } from '../../utils/tableStyles';
import RowActionMenu from '../../components/UI/RowActionMenu/RowActionMenu';
import Select, { components as selectComponents } from 'react-select';
import DataTable from 'react-data-table-component';
import { Card, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import 'rc-calendar/assets/index.css';

import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import DatePicker from '../../components/UI/DatePicker';
import TimeEditRequestModal from './timeEditRequest/TimeEditRequestModal';

import { useIsLoggedInQuery } from '../../redux/apis/authApi';
import { useGetEmployeeDropDownQuery } from '../../redux/apis/employee';
import {
  useDeleteTimeLogMutation,
  useGetProjectsQuery,
  useGetTasksListQuery,
  useGetTimeEditRequestsQuery,
  useGetTotalAndTaskLogsQuery,
  useGetTotalLogsQuery,
  useReviewTimeEditRequestMutation,
  useStartEndTimerMutation,
  useUpdateTimeLogMutation
} from '../../redux/apis/timeTrackingApi';
import { selectApiLocality, selectDisplayTimezone } from '../../redux/utils/apiLocality';
import { selectOfficeLocality } from '../../redux/Slice/officeSlice';

import { HEADER_TITLE } from '../../utils/constant/header';
import { ADMIN_HR_ROLES, ROLES } from '../../utils/constant/global';
// import { LB_ATTENDANCE_TABLE_COLS } from '../../utils/constant/labels';
import { PLACEHOLDER } from '../../utils/constant/placeHolders';
import { skipRow } from '../../utils/handler';
import { CARD_USER_ATTENDANCE } from '../../utils/constant/card';
import {
  buildTimedoctorRange,
  formatClockInTimezone,
  formatTimedoctorTime,
  toLocalDateKey,
  wallTimeOnAnchorDayToUtcMs
} from '../../utils/timedoctorAttendanceHelpers';

import moment from 'moment';
import { notification } from '../../utils/notification';
import { TOAST_TYPE } from '../../utils/constant/errorMessage';

import './AttendanceTimeTracking.scss';

const trackingTableStyles = {
  ...sharedTableStyles,
  headRow: {
    style: {
      ...sharedTableStyles.headRow.style,
      backgroundColor: '#fbfcfe',
      borderBottom: '1px solid #e7edf4',
      minHeight: '46px'
    }
  },
  headCells: {
    style: {
      ...sharedTableStyles.headCells.style,
      color: '#334155',
      fontWeight: 700,
      fontSize: '11px',
      letterSpacing: '0.05em'
    }
  },
  rows: {
    style: {
      ...sharedTableStyles.rows.style,
      minHeight: '56px',
      borderBottom: '1px solid #f1f5f9',
      fontVariantNumeric: 'tabular-nums',
      color: '#6b7280'
    },
    highlightOnHoverStyle: {
      backgroundColor: '#f5f6fb',
      borderBottomColor: '#e9edf3',
      outline: 'none'
    }
  },
  cells: {
    style: {
      ...sharedTableStyles.cells.style,
      color: '#6b7280',
      fontWeight: 600
    }
  },
  expanderRow: {
    style: {
      backgroundColor: '#fffaf5'
    }
  }
};

// const { LB_TBL_COL_START_TIME, LB_TBL_COL_END_TIME, LB_TBL_COL_DATE } = LB_ATTENDANCE_TABLE_COLS;
const { TOTAL_TIME } = CARD_USER_ATTENDANCE;
const { ATTENDANCE_TIME_TRACKING } = HEADER_TITLE;
//const { LOADING } = GLOBAL;
const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;

const toMinutesOfDay = (hhmm) => {
  const [h, m] = String(hhmm || '')
    .split(':')
    .map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

const fromMinutesOfDay = (total) => {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/** Display HH:mm (24h internal) as 12-hour clock, e.g. 14:01 → 2:01 PM */
const formatHhmmTo12h = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = String(hhmm).split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return String(hhmm);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
};

const formatHourTo12hLabel = (hour24) => {
  if (!Number.isFinite(hour24)) return '';
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return `${hour12} ${period}`;
};

/** Default AU fill/edit project: SCO — Sydney Team */
const pickAuDefaultProjectOption = (options = []) => {
  const list = Array.isArray(options) ? options : [];
  const byScoSydney = list.find((o) => {
    const key = String(o?.project?.projectKey || '').toUpperCase();
    const name = String(o?.project?.name || o?.label || '');
    return key === 'SCO' && /sydney/i.test(name);
  });
  if (byScoSydney) return byScoSydney;

  const byLabel = list.find(
    (o) => /sco/i.test(String(o?.label || '')) && /sydney/i.test(String(o?.label || ''))
  );
  if (byLabel) return byLabel;

  return list.find((o) => String(o?.project?.projectKey || '').toUpperCase() === 'SCO') || null;
};

const normalizeId = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value._id || value.id || null;
  return value;
};

const getTaskProjectId = (task) => {
  const projectField = task?.project;
  const fromProject =
    typeof projectField === 'string' || typeof projectField === 'number'
      ? projectField
      : projectField?._id || projectField?.id;
  return normalizeId(
    task?.projectId || fromProject || task?.task?.projectId || task?.task?.project?._id || null
  );
};

const taskBelongsToProject = (task, projectId) => {
  const want = normalizeId(projectId);
  if (!want) return false;
  const pid = getTaskProjectId(task);
  // API already filtered by project — keep row if projectId missing on payload
  if (!pid) return true;
  return String(pid) === String(want);
};

/** Id fields only. A project name string must not be treated as an id. */
const getExplicitProjectId = (row) =>
  normalizeId(
    row?.projectId ||
      row?.project?._id ||
      row?.project?.id ||
      row?.timelogs?.projectId ||
      row?.timelogs?.project?._id ||
      row?.timelogs?.project?.id ||
      null
  );

const rowProjectLabel = (row) => String(row?.project?.name || row?.projectName || '').trim();

const rowHasIdentifiableProject = (row) =>
  Boolean(
    getExplicitProjectId(row) || rowProjectLabel(row) || row?.project?.projectKey || row?.projectKey
  );

const findOnly = (list, predicate) => {
  const matches = list.filter(predicate);
  return matches.length === 1 ? matches[0] : null;
};

/**
 * Resolve the log's current project.
 * A known id is never swapped for a different project. SCO is only a default
 * when the log has no project at all.
 */
const pickProjectOptionForRow = (options = [], row) => {
  const list = Array.isArray(options) ? options : [];
  if (!row) return null;

  const projectId = getExplicitProjectId(row);
  if (projectId) {
    const byId = list.find((o) => String(o.value) === String(projectId));
    if (byId) return byId;
    const name = rowProjectLabel(row);
    const key = String(row?.project?.projectKey || row?.projectKey || '').trim();
    return {
      value: projectId,
      label: `${key ? `${key} — ` : ''}${name || 'Current project'}`,
      project:
        row?.project && typeof row.project === 'object'
          ? row.project
          : { _id: projectId, name: name || 'Current project', projectKey: key }
    };
  }

  const key = String(row?.project?.projectKey || row?.projectKey || '')
    .trim()
    .toUpperCase();
  if (key) {
    const byKey = findOnly(list, (o) => String(o?.project?.projectKey || '').toUpperCase() === key);
    if (byKey) return byKey;
  }

  const name = rowProjectLabel(row).toLowerCase();
  if (name) {
    return findOnly(
      list,
      (o) =>
        String(o?.project?.name || '')
          .trim()
          .toLowerCase() === name
    );
  }

  return null;
};

/** Normalize /tasks/list (and similar) response shapes into an array */
const extractTasksListRows = (res) => {
  const candidates = [
    res?.data?.result,
    res?.data?.docs,
    res?.data?.tasks,
    res?.data?.data?.docs,
    res?.data?.data?.result,
    res?.data?.data?.tasks,
    res?.data?.data?.data,
    res?.data?.data,
    res?.result,
    res?.docs,
    res?.tasks,
    res?.data,
    res
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
};

const getBrowserLocation = () =>
  new Promise((resolve) => {
    if (!navigator?.geolocation) {
      resolve({ lat: 0, long: 0 });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          long: pos.coords.longitude
        });
      },
      () => resolve({ lat: 0, long: 0 }),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  });

/** e.g. 2026-07-22T05:46:43.110+00:00 */
const toUtcOffsetIso = (ts) => {
  if (!Number.isFinite(Number(ts))) return null;
  return new Date(Number(ts)).toISOString().replace('Z', '+00:00');
};

/** Constrained hour+minute picker — only times inside [minTime, maxTime]. */
const BreakWindowTimePicker = ({
  value,
  minTime,
  maxTime,
  onChange,
  lowerBound,
  placeholder = 'Select time',
  disabled = false
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0, openUp: false });
  const rootRef = useRef(null);
  const menuRef = useRef(null);

  const minMins = toMinutesOfDay(minTime);
  const maxMins = toMinutesOfDay(maxTime);
  const lowerMins = toMinutesOfDay(lowerBound);
  const effectiveMin = Math.max(minMins ?? 0, lowerMins ?? minMins ?? 0);
  const effectiveMax = maxMins ?? 0;

  const hours = useMemo(() => {
    if (effectiveMin == null || effectiveMax == null || effectiveMin > effectiveMax) return [];
    const list = [];
    for (let h = Math.floor(effectiveMin / 60); h <= Math.floor(effectiveMax / 60); h += 1) {
      list.push(h);
    }
    return list;
  }, [effectiveMin, effectiveMax]);

  const selectedHour = value ? Number(String(value).split(':')[0]) : hours[0];
  const selectedMinute = value ? Number(String(value).split(':')[1]) : null;

  const minutes = useMemo(() => {
    if (effectiveMin == null || effectiveMax == null || !Number.isFinite(selectedHour)) return [];
    const list = [];
    for (let m = 0; m < 60; m += 1) {
      const total = selectedHour * 60 + m;
      if (total >= effectiveMin && total <= effectiveMax) list.push(m);
    }
    return list;
  }, [effectiveMin, effectiveMax, selectedHour]);

  const updateMenuPosition = () => {
    const node = rootRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const menuHeight = 220;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuHeight && rect.top > menuHeight;
    setMenuPos({
      top: openUp ? rect.top - 6 : rect.bottom + 6,
      left: rect.left,
      width: Math.max(rect.width, 180),
      openUp
    });
  };

  useEffect(() => {
    if (!open || disabled) return undefined;
    updateMenuPosition();
    const onDocClick = (e) => {
      if (rootRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) {
        return;
      }
      setOpen(false);
    };
    const onReposition = () => updateMenuPosition();
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, disabled]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const pickHour = (hour) => {
    const hourMinutes = [];
    for (let m = 0; m < 60; m += 1) {
      const total = hour * 60 + m;
      if (total >= effectiveMin && total <= effectiveMax) hourMinutes.push(m);
    }
    if (!hourMinutes.length) return;
    const minute = hourMinutes.includes(selectedMinute) ? selectedMinute : hourMinutes[0];
    onChange?.(fromMinutesOfDay(hour * 60 + minute));
  };

  const pickMinute = (minute) => {
    const hour = Number.isFinite(selectedHour) ? selectedHour : hours[0];
    if (hour == null) return;
    onChange?.(fromMinutesOfDay(hour * 60 + minute));
    setOpen(false);
  };

  const display = value ? formatHhmmTo12h(value) : placeholder;

  const menu = open
    ? createPortal(
        <div
          ref={menuRef}
          className={`att-tt-time-picker__menu${menuPos.openUp ? ' is-up' : ''}`}
          role="listbox"
          style={{
            position: 'fixed',
            top: menuPos.openUp ? 'auto' : menuPos.top,
            bottom: menuPos.openUp ? window.innerHeight - menuPos.top : 'auto',
            left: menuPos.left,
            width: menuPos.width,
            zIndex: 100050
          }}
        >
          <div className="att-tt-time-picker__col">
            <div className="att-tt-time-picker__col-title">Hour</div>
            <div className="att-tt-time-picker__list">
              {hours.map((h) => (
                <button
                  key={`h-${h}`}
                  type="button"
                  className={`att-tt-time-picker__item${h === selectedHour ? ' is-active' : ''}`}
                  onClick={() => pickHour(h)}
                >
                  {formatHourTo12hLabel(h)}
                </button>
              ))}
            </div>
          </div>
          <div className="att-tt-time-picker__col">
            <div className="att-tt-time-picker__col-title">Min</div>
            <div className="att-tt-time-picker__list">
              {minutes.map((m) => (
                <button
                  key={`m-${m}`}
                  type="button"
                  className={`att-tt-time-picker__item${m === selectedMinute ? ' is-active' : ''}`}
                  onClick={() => pickMinute(m)}
                >
                  {String(m).padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className={`att-tt-time-picker${disabled ? ' is-disabled' : ''}`} ref={rootRef}>
      <button
        type="button"
        className={`att-tt-time-picker__control${open ? ' is-open' : ''}${!value ? ' is-placeholder' : ''}`}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        disabled={disabled}
      >
        <span className="att-tt-time-picker__icon" aria-hidden>
          <i className="bi bi-clock" />
        </span>
        <span className="att-tt-time-picker__value">{display}</span>
        <span className="att-tt-time-picker__chevron" aria-hidden>
          <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
        </span>
      </button>
      {menu}
    </div>
  );
};

const initialState = {
  loading: false,
  selectType: {
    type: 'Day',
    currentDate: moment()
  },
  currentPage: 1,
  rowsPerPage: 10,
  dropdownDataStore: [],
  currentPageDropdown: 1,
  rowsPerPageDropdown: 50,
  dropdownSearch: '',
  userId: null,
  editTime: {
    isModal: false,
    rowData: null
  },
  fillBreak: {
    isModal: false,
    rowData: null,
    taskOptions: []
  },
  deleteTimeLog: {
    isModal: false,
    timeLogId: null
  }
};

function AttendanceTimeTracking() {
  const navigate = useNavigate();
  const location = useLocation();
  const deepLinkAppliedRef = useRef(false);
  const employeeSearchTimerRef = useRef(null);
  const userLocality = useSelector(selectApiLocality);
  const officeLocality = useSelector(selectOfficeLocality);
  const timeZone = useSelector(selectDisplayTimezone) || 'Asia/Kolkata';

  const [state, setState] = useState(initialState);
  const [fromDate, setFromDate] = useState(
    () => buildTimedoctorRange(moment(), timeZone, 'Day').from
  );
  const [toDate, setToDate] = useState(() => buildTimedoctorRange(moment(), timeZone, 'Day').to);

  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [reasonFieldKey, setReasonFieldKey] = useState(0);
  const editReasonRef = useRef(null);
  /** Which break gap the Fill break form was initialized for. */
  const fillBreakOpenKeyRef = useRef(null);
  /** True after the user picks a project in Fill break. */
  const fillBreakProjectTouchedRef = useRef(false);
  /** True after the user picks a project in Update time. */
  const editProjectTouchedRef = useRef(false);
  /** Update time prefills the row task only once per open. */
  const editTaskPrefilledRef = useRef(false);
  /** Timelog currently initialized in Update time. Same row must not reset the form. */
  const editOpenKeyRef = useRef(null);
  /** Project id last seeded for the open Update time row. */
  const editAutoProjectIdRef = useRef(null);
  const [selectedBreakTask, setSelectedBreakTask] = useState(null);
  const [selectedBreakProject, setSelectedBreakProject] = useState(null);
  const [breakTaskError, setBreakTaskError] = useState('');
  const [breakProjectError, setBreakProjectError] = useState('');
  const [breakTimeMin, setBreakTimeMin] = useState('');
  const [breakTimeMax, setBreakTimeMax] = useState('');
  /** Edit modal: cannot go before previous log end / after next log start */
  const [editBoundMin, setEditBoundMin] = useState('00:00');
  const [editBoundMax, setEditBoundMax] = useState('23:59');
  const frozenTableDataRef = useRef(null);
  /** IN-only: create time edit request modal */
  const [isTimeEditRequestOpen, setIsTimeEditRequestOpen] = useState(false);
  const [timeEditRequestPrefill, setTimeEditRequestPrefill] = useState(null);
  /** IN Admin/HR: approve/reject pending request from day list */
  const [editRequestReview, setEditRequestReview] = useState({
    open: false,
    row: null,
    status: '',
    note: ''
  });
  const isAuUser = useMemo(
    () => (Array.isArray(userLocality) ? userLocality : []).includes('AU'),
    [userLocality]
  );
  const isAuOffice = useMemo(
    () => (Array.isArray(officeLocality) ? officeLocality : []).includes('AU'),
    [officeLocality]
  );
  const isInUser = useMemo(
    () => (Array.isArray(userLocality) ? userLocality : []).includes('IN'),
    [userLocality]
  );
  const isInOffice = useMemo(
    () => (Array.isArray(officeLocality) ? officeLocality : []).includes('IN'),
    [officeLocality]
  );
  /** Break button + AU edit rules when locality is AU (API locality or office). */
  const isAuContext = isAuUser || isAuOffice;
  /**
   * IN edit-request flow only when AU is NOT present.
   * Product rule: if both IN+AU, prefer AU direct flow.
   */
  const isInContext = !isAuContext && (isInUser || isInOffice);
  /** Break gaps: AU (fill break) + IN (time edit request from break). */
  const showBreakRows = isAuContext || isInContext;

  const { data } = useIsLoggedInQuery();
  const values = data?.data;
  const roleName = values?.hrxRole?.roleName;
  const isAdminOrHr = ADMIN_HR_ROLES.includes(roleName);

  /**
   * Admin/HR can pick any employee (AU + IN). Needed so HR can fill an employee's break
   * without going through Time Edit Request.
   */
  const canSelectEmployee = isAdminOrHr;
  /** Direct PMS edit/delete — AU users/office, or IN Admin only. Never IN employees. */
  const isEditable = isAuContext || roleName === ROLES.ADMIN;

  const selectedAssigneeId =
    state?.userId && (canSelectEmployee || isAdminOrHr) ? state.userId : values?._id;

  /*   const {
    data: employeeData,
    isLoading,
    isFetching,
    refetch: refetchEmployee
  } = canSelectEmployee
    ? useGetEmployeeQuery({
        search: '',
        skip: skipRow(state.currentPage, state.rowsPerPage),
        limit: state.rowsPerPage
      })
    : { data: null, isLoading: false, isFetching: false, refetch: () => {} }; */

  const {
    data: dropdownData,
    isFetching: isEmployeeDropdownFetching,
    refetch: employeeDropDownQuery
  } = canSelectEmployee
    ? useGetEmployeeDropDownQuery({
        skip: skipRow(state.currentPageDropdown, state.rowsPerPageDropdown),
        limit: state.rowsPerPageDropdown,
        search: state.dropdownSearch,
        sort: 'name',
        sortType: 'asc'
      })
    : { data: null, isLoading: false, isFetching: false, refetch: () => {} };

  const {
    data: totalAndTaskLogs,
    isLoading: isLogsLoading,
    isFetching: isLogsFetching,
    refetch: getTotalAndTaskLogs
  } = useGetTotalAndTaskLogsQuery(
    {
      assigneeId: selectedAssigneeId,
      from: fromDate,
      to: toDate,
      skip: 0,
      limit: 35
    },
    {
      skip: false
    }
  );

  const { data: totalLogs, refetch: getTotalLogs } = useGetTotalLogsQuery(
    {
      assigneeId: selectedAssigneeId,
      type: 'totalLog',
      from: fromDate,
      to: toDate
    },
    {
      skip: false
    }
  );

  // IN only: load edit requests for selected Day/Week/Month range (from/to)
  const editRequestRange = useMemo(() => {
    const fromMs = Date.parse(fromDate);
    const toMs = Date.parse(toDate);
    return {
      from: Number.isFinite(fromMs) ? fromMs : undefined,
      to: Number.isFinite(toMs) ? toMs : undefined
    };
  }, [fromDate, toDate]);

  const { data: timeEditRequestsRes } = useGetTimeEditRequestsQuery(
    {
      page: 1,
      limit: 100,
      ...(editRequestRange.from != null ? { from: editRequestRange.from } : {}),
      ...(editRequestRange.to != null ? { to: editRequestRange.to } : {}),
      ...(isAdminOrHr && selectedAssigneeId ? { userId: selectedAssigneeId } : {})
    },
    {
      skip: !isInContext || !selectedAssigneeId || editRequestRange.from == null,
      refetchOnMountOrArgChange: true
    }
  );

  const inEditRequests = useMemo(() => {
    if (!isInContext) return [];
    const candidates = [
      timeEditRequestsRes?.data?.data,
      timeEditRequestsRes?.data?.docs,
      timeEditRequestsRes?.data?.result,
      timeEditRequestsRes?.data,
      timeEditRequestsRes?.docs
    ];
    for (const c of candidates) {
      if (Array.isArray(c)) return c;
    }
    return [];
  }, [isInContext, timeEditRequestsRes]);

  const [updateTimeLog, { isLoading: isUpdating }] = useUpdateTimeLogMutation();
  const [startEndTimer, { isLoading: isCreating }] = useStartEndTimerMutation();
  const [deleteTimeLog, { isLoading: isDeleting }] = useDeleteTimeLogMutation();
  const [reviewTimeEditRequest, { isLoading: isReviewingEditRequest }] =
    useReviewTimeEditRequestMutation();

  const showProjectTaskPickers = state.fillBreak.isModal || (state.editTime.isModal && isAuContext);

  const { data: projectsRes, isFetching: isProjectsLoading } = useGetProjectsQuery(
    { skip: 0, limit: 500 },
    { skip: !showProjectTaskPickers }
  );

  const { data: tasksListRes, isFetching: isTasksLoading } = useGetTasksListQuery(
    {
      projectId: normalizeId(selectedBreakProject?.value),
      assigneeId: normalizeId(selectedAssigneeId),
      skip: 0,
      limit: 200
    },
    {
      skip:
        !showProjectTaskPickers ||
        !normalizeId(selectedBreakProject?.value) ||
        !normalizeId(selectedAssigneeId)
    }
  );

  const projectOptions = useMemo(() => {
    const list =
      projectsRes?.data?.result ||
      projectsRes?.data?.data ||
      projectsRes?.data?.docs ||
      projectsRes?.data ||
      projectsRes?.result ||
      [];
    const rows = Array.isArray(list) ? list : [];
    return rows
      .map((p) => {
        const id = p?._id || p?.id;
        if (!id) return null;
        const key = p?.projectKey ? `${p.projectKey} — ` : '';
        return {
          value: id,
          label: `${key}${p?.name || 'Untitled project'}`,
          project: p
        };
      })
      .filter(Boolean)
      .sort((a, b) =>
        String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' })
      );
  }, [projectsRes]);

  const fillBreakTaskOptions = useMemo(() => {
    const projectId = normalizeId(selectedBreakProject?.value);
    const rows = extractTasksListRows(tasksListRes);
    const opts = rows
      .map((task) => {
        const taskId = normalizeId(task?.taskId || task?._id || task?.id);
        if (!taskId) return null;
        // Soft guard only when task carries an explicit different projectId
        if (projectId && getTaskProjectId(task) && !taskBelongsToProject(task, projectId)) {
          return null;
        }
        return {
          value: taskId,
          label: `${task?.taskKey ? `${task.taskKey} — ` : ''}${task?.title || 'Untitled'}`,
          task: { ...task, taskId, projectId: projectId || getTaskProjectId(task) }
        };
      })
      .filter(Boolean)
      .sort((a, b) =>
        String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' })
      );

    if (
      selectedBreakTask?.value &&
      (!projectId ||
        taskBelongsToProject(selectedBreakTask.task || selectedBreakTask, projectId)) &&
      !opts.some((o) => String(o.value) === String(selectedBreakTask.value))
    ) {
      opts.unshift(selectedBreakTask);
    }
    return opts;
  }, [tasksListRes, selectedBreakProject?.value, selectedBreakTask]);

  /*  function secondsToHms(total) {
    let d =
      total?.length > 0
        ? total?.reduce((accumulator, currentValue) => accumulator + currentValue?.totalLog, 0)
        : total;

    d = Number(d);
    var h = Math.floor(d / 3600);
    var m = Math.floor((d % 3600) / 60);
    var s = Math.floor((d % 3600) % 60);

    var hDisplay = h > 0 ? h + 'h ' : '';
    var mDisplay = m > 0 ? m + 'm ' : '';
    var sDisplay = s > 0 ? s + 's ' : '';
    return hDisplay + mDisplay + sDisplay;
  } */

  function msToHms(ms) {
    if (!ms || isNaN(ms)) return '0h 0m 0s';
    let totalSeconds = Math.floor(ms / 1000); // ✅ convert ms → seconds
    let h = Math.floor(totalSeconds / 3600);
    let m = Math.floor((totalSeconds % 3600) / 60);
    let s = totalSeconds % 60;

    let hDisplay = h > 0 ? h + 'h ' : '';
    let mDisplay = m > 0 ? m + 'm ' : '';
    let sDisplay = s > 0 ? s + 's ' : '';
    return hDisplay + mDisplay + sDisplay;
  }

  function trackedTimeParts(ms) {
    const totalSeconds = Math.floor((Number(ms) || 0) / 1000);
    return [
      { key: 'h', value: Math.floor(totalSeconds / 3600) },
      { key: 'm', value: Math.floor((totalSeconds % 3600) / 60) },
      { key: 's', value: totalSeconds % 60 }
    ];
  }

  useEffect(() => {
    getTotalAndTaskLogs();
    getTotalLogs();
  }, [fromDate, toDate, state.userId]);

  // Deep link from Time Edit Req: ?userId=&userName=&date=YYYY-MM-DD
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    if (!values?._id) return;

    const params = new URLSearchParams(location.search || '');
    const linkUserId = params.get('userId');
    const linkUserName = params.get('userName');
    const linkDate = params.get('date');
    if (!linkUserId && !linkDate) return;

    deepLinkAppliedRef.current = true;

    let currentDate = moment();
    if (linkDate && moment(linkDate, 'YYYY-MM-DD', true).isValid()) {
      currentDate = moment(linkDate, 'YYYY-MM-DD');
    }

    const canApplyUser = Boolean(linkUserId) && (canSelectEmployee || isAdminOrHr);

    setState((prev) => {
      const next = {
        ...prev,
        selectType: { type: 'Day', currentDate },
        ...(canApplyUser ? { userId: linkUserId } : {})
      };

      if (canApplyUser) {
        const store = Array.isArray(prev.dropdownDataStore) ? prev.dropdownDataStore : [];
        const exists = store.some((e) => String(e?._id) === String(linkUserId));
        if (!exists) {
          next.dropdownDataStore = [
            { _id: linkUserId, name: linkUserName || 'Employee' },
            ...store
          ];
        }
      }

      return next;
    });

    navigate('/attendance-time-tracking', { replace: true });
  }, [location.search, values?._id, canSelectEmployee, isAdminOrHr, navigate]);

  const formatClockInput = (timestamp) => formatClockInTimezone(timestamp, timeZone);

  const formatDateInput = (timestamp) => toLocalDateKey(timestamp, timeZone) || '';

  const buildTaskOptions = (tasks = []) => {
    const map = new Map();
    (tasks || []).forEach((task) => {
      if (task?.isBreakRow) return;
      const taskId = task?.taskId || task?.task?._id || task?.task?.id || task?._id;
      if (!taskId || map.has(String(taskId))) return;
      map.set(String(taskId), {
        value: taskId,
        label: `${task?.taskKey ? `${task.taskKey} — ` : ''}${task?.title || 'Untitled'}`,
        task: { ...task, taskId }
      });
    });
    return Array.from(map.values());
  };

  useEffect(() => {
    if (
      state.editTime.isModal &&
      state.editTime.rowData?.timelogs &&
      !state.editTime.rowData?.isBreakRow
    ) {
      const row = state.editTime.rowData;
      const { start, end } = row.timelogs;

      // Adjacent logs: edit window = previous end → next start (no override)
      const siblings = (Array.isArray(row.dayTasks) ? row.dayTasks : [])
        .filter((t) => !t?.isBreakRow && Number.isFinite(Number(t?.timelogs?.start)))
        .sort((a, b) => Number(a.timelogs.start) - Number(b.timelogs.start));
      const selfId = row.timelogs?._id;
      const idx = siblings.findIndex((t) => t?.timelogs?._id === selfId);
      const prev = idx > 0 ? siblings[idx - 1] : null;
      const next = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null;
      const prevEndTs = prev ? Number(prev.timelogs?.end ?? prev.timelogs?.lastCheck) : null;
      const nextStartTs = next ? Number(next.timelogs?.start) : null;
      const boundMin =
        Number.isFinite(prevEndTs) && prevEndTs > 0 ? formatClockInput(prevEndTs) : '00:00';
      const boundMax =
        Number.isFinite(nextStartTs) && nextStartTs > 0 ? formatClockInput(nextStartTs) : '23:59';
      setEditBoundMin(boundMin);
      setEditBoundMax(boundMax);

      const clampToBounds = (hhmm) => {
        if (!hhmm) return hhmm;
        const v = toMinutesOfDay(hhmm);
        const lo = toMinutesOfDay(boundMin) ?? 0;
        const hi = toMinutesOfDay(boundMax) ?? 23 * 60 + 59;
        if (v == null) return hhmm;
        return fromMinutesOfDay(Math.max(lo, Math.min(hi, v)));
      };

      const nextStart = start ? clampToBounds(formatClockInput(start)) : '';
      let nextEnd = end ? clampToBounds(formatClockInput(end)) : '';
      if (nextStart && nextEnd && toMinutesOfDay(nextEnd) < toMinutesOfDay(nextStart)) {
        nextEnd = nextStart;
      }

      setStartTime(nextStart);
      setEndTime(nextEnd);
      setSelectedDate(start ? formatDateInput(start) : '');
      setReasonError('');
      setReasonFieldKey((k) => k + 1);
      setBreakTimeMin('');
      setBreakTimeMax('');
      setBreakTaskError('');
      setBreakProjectError('');

      const openKey = String(row?.timelogs?._id || '');
      if (editOpenKeyRef.current !== openKey) {
        editOpenKeyRef.current = openKey;
        editProjectTouchedRef.current = false;
        editTaskPrefilledRef.current = false;
        editAutoProjectIdRef.current = null;
        setSelectedBreakTask(null);
        setSelectedBreakProject(null);
      }
    } else {
      editOpenKeyRef.current = null;
      editProjectTouchedRef.current = false;
      editTaskPrefilledRef.current = false;
      editAutoProjectIdRef.current = null;
    }
  }, [state.editTime, isAuContext]);

  useEffect(() => {
    if (!state.fillBreak.isModal || !state.fillBreak.rowData?.timelogs) {
      fillBreakOpenKeyRef.current = null;
      fillBreakProjectTouchedRef.current = false;
      return;
    }

    const row = state.fillBreak.rowData;
    const openKey = [
      row?.timelogs?._id,
      row?.breakWindowStart ?? row?.timelogs?.start,
      row?.breakWindowEnd ?? row?.timelogs?.end
    ].join('|');
    // Same open gap: do not wipe a project/task the user already chose.
    if (fillBreakOpenKeyRef.current === openKey) return;
    fillBreakOpenKeyRef.current = openKey;
    fillBreakProjectTouchedRef.current = false;

    const { start, end } = row.timelogs;
    setStartTime(start ? formatClockInput(start) : '');
    setEndTime(end ? formatClockInput(end) : '');
    setSelectedDate(start ? formatDateInput(start) : '');
    setBreakTimeMin(start ? formatClockInput(start) : '');
    setBreakTimeMax(end ? formatClockInput(end) : '');
    setReasonError('');
    setReasonFieldKey((k) => k + 1);
    setSelectedBreakTask(null);
    setSelectedBreakProject(null);
    setBreakTaskError('');
    setBreakProjectError('');
  }, [state.fillBreak]);

  // AU: seed a project once, then keep the user's choice.
  // Update time uses the log's project, or SCO — Sydney when the log has none.
  useEffect(() => {
    if (!isAuContext) return;
    const fillOpen = Boolean(state.fillBreak.isModal);
    const editOpen = Boolean(state.editTime.isModal);
    if (!fillOpen && !editOpen) return;

    if (editOpen && !fillOpen) {
      const row = state.editTime.rowData;
      setSelectedBreakProject((prev) => {
        if (editProjectTouchedRef.current && prev?.value) return prev;
        const fromRow = pickProjectOptionForRow(projectOptions, row);
        // SCO only when this log has no project. Never replace a known project.
        const next =
          fromRow ||
          (rowHasIdentifiableProject(row) ? null : pickAuDefaultProjectOption(projectOptions));
        if (!next) return prev;
        const autoId = editAutoProjectIdRef.current;
        // New open (auto id cleared) replaces a leftover selection. Later refreshes
        // only replace the untouched seeded value, so a user choice stays put.
        if (!autoId || !prev?.value || String(prev.value) === String(autoId)) {
          editAutoProjectIdRef.current = next.value;
          return next;
        }
        return prev;
      });
      setBreakProjectError('');
      return;
    }

    const def = pickAuDefaultProjectOption(projectOptions);
    if (!def || !fillOpen) return;
    setSelectedBreakProject((prev) => {
      if (fillBreakProjectTouchedRef.current && prev?.value) return prev;
      if (prev?.value && String(prev.value) === String(def.value)) return prev;
      return def;
    });
    setBreakProjectError('');
  }, [
    isAuContext,
    state.fillBreak.isModal,
    state.editTime.isModal,
    state.editTime.rowData,
    projectOptions
  ]);

  // Fill break: drop a task that belongs to a project the user just left.
  useEffect(() => {
    if (!state.fillBreak.isModal || state.editTime.isModal) return;
    const projectId = normalizeId(selectedBreakProject?.value);
    setSelectedBreakTask((task) => {
      if (!task?.value) return task;
      if (!projectId) return null;
      if (taskBelongsToProject(task.task || task, projectId)) return task;
      return null;
    });
  }, [state.fillBreak.isModal, state.editTime.isModal, selectedBreakProject?.value]);

  // AU Update time: prefill the row task once. A later project change is left empty.
  useEffect(() => {
    if (!isAuContext || !state.editTime.isModal || !state.editTime.rowData) return;
    if (!selectedBreakProject?.value) return;
    if (editProjectTouchedRef.current || editTaskPrefilledRef.current) return;

    const row = state.editTime.rowData;
    const projectId = normalizeId(selectedBreakProject.value);
    const rowProjectId = getExplicitProjectId(row);

    if (rowProjectId && String(rowProjectId) !== String(projectId)) return;

    const taskId = normalizeId(row.taskId || row.task?._id || row.task?.id || row.timelogs?.taskId);
    if (!taskId) {
      editTaskPrefilledRef.current = true;
      return;
    }

    const fromList = fillBreakTaskOptions.find((o) => String(o.value) === String(taskId));
    if (!fromList && isTasksLoading) return;

    editTaskPrefilledRef.current = true;
    setSelectedBreakTask((prev) => {
      if (prev?.value) return prev;
      if (fromList) return fromList;
      return {
        value: taskId,
        label: `${row.taskKey ? `${row.taskKey} — ` : ''}${row.title || 'Untitled'}`,
        task: { ...row, taskId, projectId }
      };
    });
  }, [
    isAuContext,
    state.editTime.isModal,
    state.editTime.rowData,
    selectedBreakProject?.value,
    fillBreakTaskOptions,
    isTasksLoading
  ]);

  const openEditRequestReview = (row, status) => {
    setEditRequestReview({ open: true, row, status, note: '' });
  };

  const closeEditRequestReview = () => {
    setEditRequestReview({ open: false, row: null, status: '', note: '' });
  };

  const handleEditRequestReviewSave = async () => {
    const requestId = editRequestReview.row?.editRequestId;
    if (!requestId || !editRequestReview.status) {
      notification('Request id missing.', TOAST_ERROR);
      return;
    }

    try {
      const res = await reviewTimeEditRequest({
        id: requestId,
        status: editRequestReview.status,
        reviewNote: String(editRequestReview.note || '').trim()
      }).unwrap();
      notification(
        res?.statusMessage ||
          (editRequestReview.status === 'Approved'
            ? 'Request approved — time log created.'
            : 'Request rejected.'),
        TOAST_SUCCESS
      );
      closeEditRequestReview();
      getTotalAndTaskLogs();
      getTotalLogs();
    } catch (err) {
      notification(
        err?.data?.statusMessage ||
          err?.data?.message ||
          err?.data?.data?.message ||
          'Failed to review request.',
        TOAST_ERROR
      );
    }
  };

  const closeEditModal = () => {
    setStartTime('');
    setEndTime('');
    setSelectedDate('');
    setReasonError('');
    setBreakTimeMin('');
    setBreakTimeMax('');
    setEditBoundMin('00:00');
    setEditBoundMax('23:59');
    setSelectedBreakTask(null);
    setSelectedBreakProject(null);
    setBreakTaskError('');
    setBreakProjectError('');
    setState((prev) => ({
      ...prev,
      editTime: { isModal: false, rowData: null },
      fillBreak: { isModal: false, rowData: null, taskOptions: [] }
    }));
  };

  const openFillBreakModal = (row, dayTasks = []) => {
    setState((prev) => ({
      ...prev,
      fillBreak: {
        isModal: true,
        rowData: row,
        taskOptions: buildTaskOptions(dayTasks)
      }
    }));
  };

  const openDeleteTimeLogModal = (timeLogId) => {
    setState((prev) => ({
      ...prev,
      deleteTimeLog: { isModal: true, timeLogId }
    }));
  };

  const closeDeleteTimeLogModal = () => {
    setState((prev) => ({
      ...prev,
      deleteTimeLog: { isModal: false, timeLogId: null }
    }));
  };

  const handleDeleteTimeLog = async () => {
    const timeLogId = state.deleteTimeLog.timeLogId;
    if (!timeLogId) return;

    try {
      const res = await deleteTimeLog(timeLogId).unwrap();

      if (res?.success) {
        notification(res?.statusMessage || 'Time log deleted successfully.', TOAST_SUCCESS);
        closeDeleteTimeLogModal();
        getTotalAndTaskLogs();
        getTotalLogs();
      } else {
        notification(res?.statusMessage || 'Failed to delete time log.', TOAST_ERROR);
      }
    } catch (err) {
      console.error('Delete failed:', err);
      notification('Something went wrong while deleting time log.', TOAST_ERROR);
    }
  };

  const groupedData = totalAndTaskLogs?.data?.data || {};

  /**
   * Header TOTAL TIME TRACKED — always sum from the same day/task groups as the table.
   * Do not use type=totalLog unit heuristics (API often returns ms; treating as seconds
   * inflated totals e.g. ~41m → ~674h).
   */
  const totalTrackedMs = useMemo(() => {
    const totalMins = Object.values(groupedData).reduce((sum, group) => {
      const tasks = Array.isArray(group?.data) ? group.data : [];
      return sum + tasks.reduce((s, t) => s + (Number(t?.duration) || 0), 0);
    }, 0);

    if (totalMins > 0) return totalMins * 60 * 1000;

    // Fallback if groups expose ms totals only
    const fromGroupMs = Object.values(groupedData).reduce((sum, group) => {
      const n = Number(group?.totalLog ?? group?.totalMilliseconds ?? 0);
      return Number.isFinite(n) && n > 0 ? sum + n : sum;
    }, 0);

    return fromGroupMs > 0 ? fromGroupMs : 0;
  }, [groupedData]);

  const fmtClock = (ts) => (ts ? formatTimedoctorTime(ts, timeZone) : '-');
  const EIGHT_HOURS_MS = 8 * 60 * 60 * 1000;

  // Theme color only (no task-wise colors)
  // Match Dashboard Observant (OBS) timeline color
  const WORK_GRADIENT = 'linear-gradient(90deg, #a855f7 0%, #6366f1 100%)';

  const buildTimelineSegments = (spanStartMs, spanEndMs, tasks = []) => {
    const start = Number(spanStartMs);
    const end = Number(spanEndMs);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];

    const span = end - start;
    const src = (Array.isArray(tasks) ? tasks : []).filter(
      (t) => !t?.isBreakRow && !t?.isEditRequestRow
    );

    const work = src
      .map((t) => {
        const s = Number(t?.timelogs?.start);
        const e = Number(t?.timelogs?.end ?? t?.timelogs?.lastCheck ?? Date.now());
        if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return null;

        const clampedStart = Math.max(start, Math.min(end, s));
        const clampedEnd = Math.max(start, Math.min(end, e));
        if (clampedEnd <= clampedStart) return null;

        const taskKey = t?.taskKey ? `(${t.taskKey}) ` : '';
        const title = t?.title || 'Task';
        const isRunning = !t?.timelogs?.end;
        return {
          kind: 'work',
          startMs: clampedStart,
          endMs: clampedEnd,
          isRunning,
          color: WORK_GRADIENT,
          label: `${taskKey}${title} • ${fmtClock(clampedStart)} – ${isRunning ? 'Now' : fmtClock(clampedEnd)}`
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.startMs - b.startMs);

    if (!work.length) return [];

    // Build break segments from gaps between work intervals (within span)
    const minGapMs = 1 * 60 * 1000; // only show breaks ≥ 1 minute
    const segs = [];
    let cursor = start;

    for (let i = 0; i < work.length; i += 1) {
      const w = work[i];

      const gapStart = cursor;
      const gapEnd = Math.min(end, w.startMs);
      if (gapEnd - gapStart >= minGapMs) {
        segs.push({
          kind: 'break',
          startMs: gapStart,
          endMs: gapEnd,
          label: `Break • ${fmtClock(gapStart)} – ${fmtClock(gapEnd)}`
        });
      }

      segs.push(w);
      cursor = Math.max(cursor, w.endMs);
    }

    // trailing gap until end
    if (end - cursor >= minGapMs) {
      segs.push({
        kind: 'break',
        startMs: cursor,
        endMs: end,
        label: `Break • ${fmtClock(cursor)} – ${fmtClock(end)}`
      });
    }

    // Convert to left/width for rendering
    return segs
      .map((s) => {
        const left = (s.startMs - start) / span;
        const width = (s.endMs - s.startMs) / span;
        return {
          kind: s.kind,
          left: Math.max(0, Math.min(1, left)),
          width: Math.max(0, Math.min(1, width)),
          color: s.color,
          isRunning: s.isRunning,
          endLeft: (s.endMs - start) / span,
          label: s.label
        };
      })
      .filter((s) => s.width > 0);
  };

  const buildExpandedRowsWithBreaks = (tasks = [], dateKey = '') => {
    const sortedTasks = [...tasks]
      .filter((task) => Number.isFinite(Number(task?.timelogs?.start)))
      .sort((a, b) => Number(a?.timelogs?.start) - Number(b?.timelogs?.start));

    const withDayTasks = (task) => ({ ...task, dayTasks: sortedTasks });

    if (!showBreakRows || sortedTasks.length <= 1) {
      return sortedTasks.map(withDayTasks);
    }

    const rows = [];
    const minGapMs = 1 * 60 * 1000; // only show break rows ≥ 1 minute

    sortedTasks.forEach((task, index) => {
      rows.push(withDayTasks(task));

      if (index === sortedTasks.length - 1) return;

      const currentEnd = Number(task?.timelogs?.end ?? task?.timelogs?.lastCheck ?? Date.now());
      const nextStart = Number(sortedTasks[index + 1]?.timelogs?.start);
      const gapMs = nextStart - currentEnd;

      if (!Number.isFinite(currentEnd) || !Number.isFinite(nextStart) || gapMs < minGapMs) {
        return;
      }

      rows.push({
        _id: `break-${task?.timelogs?._id || index}-${sortedTasks[index + 1]?.timelogs?._id || index + 1}`,
        isBreakRow: true,
        title: 'Break',
        taskKey: 'BREAK',
        dateKey,
        dayTasks: sortedTasks,
        durationFormatted: msToHms(gapMs),
        breakWindowStart: currentEnd,
        breakWindowEnd: nextStart,
        timelogs: {
          start: currentEnd,
          end: nextStart
        }
      });
    });

    return rows;
  };

  /** Normalize parent table dateKey (e.g. 25-Jun-2026) → YYYY-MM-DD for matching requests */
  const normalizeDateKeyToYmd = (dateKey) => {
    if (!dateKey) return null;
    const raw = String(dateKey);
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const m = moment(raw, ['DD-MMM-YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY'], true);
    if (m.isValid()) return m.format('YYYY-MM-DD');
    return null;
  };

  /** IN only: append edit-request rows into day expanded list (AU untouched) */
  const mergeEditRequestsIntoDayRows = (rows = [], dateKey = '', dayTasks = []) => {
    if (!isInContext || !inEditRequests.length) return rows;

    const dayYmd = normalizeDateKeyToYmd(dateKey);
    const logIds = new Set(
      (dayTasks || []).map((t) => String(t?.timelogs?._id || '')).filter(Boolean)
    );
    const logStarts = new Set(
      (dayTasks || [])
        .map((t) => Number(t?.timelogs?.start))
        .filter((n) => Number.isFinite(n))
        .map((n) => Math.floor(n / 1000))
    );

    const requestRows = inEditRequests
      .filter((req) => {
        const status = String(req?.status || '').toLowerCase();
        const reqYmd =
          toLocalDateKey(req?.date, timeZone) ||
          toLocalDateKey(req?.requestedStart ?? req?.start, timeZone);
        if (!dayYmd || !reqYmd || reqYmd !== dayYmd) return false;

        // Approved + already created PMS log → don't duplicate if log is on this day
        // (Rejected never creates a log — always keep the request row visible)
        if (status !== 'rejected') {
          const timeLogId = req?.timeLogId || req?.timelogId;
          if (timeLogId && logIds.has(String(timeLogId))) return false;

          const startSec = Math.floor(Number(req?.requestedStart ?? req?.start) / 1000);
          if (Number.isFinite(startSec) && logStarts.has(startSec)) return false;
        }

        return true;
      })
      .map((req) => {
        const start = Number(req?.requestedStart ?? req?.start);
        const end = Number(req?.requestedEnd ?? req?.end);
        const gapMs =
          Number.isFinite(start) && Number.isFinite(end) && end > start ? end - start : 0;
        return {
          _id: `edit-req-${req?._id || req?.id || start}`,
          isEditRequestRow: true,
          editRequestStatus: req?.status || 'Pending',
          editRequestId: req?._id || req?.id,
          title: req?.taskName || 'Time edit request',
          taskKey: 'REQ',
          project: { name: req?.projectName || '—' },
          projectName: req?.projectName || '—',
          dateKey,
          reason: req?.reason || '',
          durationFormatted: gapMs ? msToHms(gapMs) : '—',
          timelogs: {
            start: Number.isFinite(start) ? start : null,
            end: Number.isFinite(end) ? end : null,
            reason: req?.reason || ''
          }
        };
      });

    if (!requestRows.length) return rows;

    const toWindow = (r) => ({
      start: Number(r?.timelogs?.start),
      end: Number(r?.timelogs?.end)
    });
    const isValidWindow = (w) =>
      Number.isFinite(w.start) && Number.isFinite(w.end) && w.end > w.start;

    // Any request (Pending / Approved / Rejected) cuts the Break — leftover ≥ 1m still shows
    // e.g. Pending 12:30–12:31 on gap 12:30–12:56 → Break 12:31–12:56
    const cutWindows = requestRows.map(toWindow).filter(isValidWindow);

    const subtractIntervals = (start, end, cuts) => {
      let segments = [{ start, end }];
      cuts.forEach((cut) => {
        segments = segments.flatMap((seg) => {
          if (cut.end <= seg.start || cut.start >= seg.end) return [seg];
          const out = [];
          if (cut.start > seg.start) out.push({ start: seg.start, end: cut.start });
          if (cut.end < seg.end) out.push({ start: cut.end, end: seg.end });
          return out.filter((s) => s.end > s.start);
        });
      });
      return segments;
    };

    const minRemainingBreakMs = 1 * 60 * 1000; // only leftover break ≥ 1 minute

    const adjustedRows = rows.flatMap((row) => {
      if (!row?.isBreakRow) return [row];

      const bStart = Number(row?.timelogs?.start ?? row?.breakWindowStart);
      const bEnd = Number(row?.timelogs?.end ?? row?.breakWindowEnd);
      if (!Number.isFinite(bStart) || !Number.isFinite(bEnd) || bEnd <= bStart) {
        return [row];
      }

      const overlappingCuts = cutWindows.filter((w) => w.start < bEnd && w.end > bStart);
      if (!overlappingCuts.length) return [row];

      return subtractIntervals(bStart, bEnd, overlappingCuts)
        .filter((s) => s.end - s.start >= minRemainingBreakMs)
        .map((s, idx) => ({
          ...row,
          _id: `${row._id}-remain-${idx}`,
          durationFormatted: msToHms(s.end - s.start),
          breakWindowStart: s.start,
          breakWindowEnd: s.end,
          timelogs: {
            ...row.timelogs,
            start: s.start,
            end: s.end
          }
        }));
    });

    return [...adjustedRows, ...requestRows].sort((a, b) => {
      const as = Number(a?.timelogs?.start) || 0;
      const bs = Number(b?.timelogs?.start) || 0;
      return as - bs;
    });
  };

  const TimelineBar = ({ row }) => {
    const spanStartMs = row?.spanStartMs;
    const spanEndMs = row?.spanEndMs;
    const tasks = row?.rows;
    const segs = buildTimelineSegments(spanStartMs, spanEndMs, tasks);

    if (!segs.length) {
      return <span className="text-muted">—</span>;
    }

    return (
      <div className="att-tt-timeline" aria-label="Day timeline">
        <div className="att-tt-timeline__bar" aria-hidden>
          {segs.slice(0, 80).map((s, idx, arr) => {
            const isFirst = idx === 0;
            const isLast = idx === arr.length - 1;
            const roundClass =
              (isFirst ? ' att-tt-timeline__seg--start' : '') +
              (isLast ? ' att-tt-timeline__seg--end' : '');

            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`seg-${idx}`}
                className={`att-tt-timeline__seg${s.kind === 'break' ? ' att-tt-timeline__seg--break' : ''}${roundClass}`}
                style={{
                  left: `${s.left * 100}%`,
                  width: `${s.width * 100}%`,
                  background: s.kind === 'break' ? undefined : s.color
                }}
                title={s.label}
              />
            );
          })}
        </div>
        <div className="att-tt-timeline__range" aria-hidden>
          <span>{fmtClock(spanStartMs)}</span>
          <span>{fmtClock(spanEndMs)}</span>
        </div>
      </div>
    );
  };

  // Freeze table while any modal open — select/typing must not re-render/animate rows
  const isModalOpen =
    state.editTime.isModal ||
    state.fillBreak.isModal ||
    isTimeEditRequestOpen ||
    editRequestReview.open;

  const liveTableData = useMemo(() => {
    return Object.keys(groupedData).map((dateKey) => {
      const group = groupedData[dateKey] || {};
      const tasks = group?.data || [];

      const totalDurationMins = tasks.reduce((sum, t) => sum + (Number(t.duration) || 0), 0);
      let totalMins = Math.round(totalDurationMins);
      const totalHours = Math.floor(totalMins / 60);
      totalMins = totalMins % 60;
      const totalFormatted = `${totalHours}h ${totalMins}m`;

      const startTimeLabel = group.startTime
        ? formatTimedoctorTime(group.startTime, timeZone)
        : '-';
      const endTimeLabel = group.endTime ? formatTimedoctorTime(group.endTime, timeZone) : '-';

      return {
        dateKey,
        startTime: startTimeLabel,
        endTime: endTimeLabel,
        totalFormatted,
        rows: mergeEditRequestsIntoDayRows(
          buildExpandedRowsWithBreaks(tasks, dateKey),
          dateKey,
          tasks
        ),
        spanStartMs: group.startTime || null,
        spanEndMs: group.startTime ? Number(group.startTime) + EIGHT_HOURS_MS : null
      };
    });
  }, [groupedData, showBreakRows, timeZone, isInContext, inEditRequests]);

  useEffect(() => {
    if (!isModalOpen) {
      frozenTableDataRef.current = liveTableData;
    }
  }, [isModalOpen, liveTableData]);

  const tableData =
    isModalOpen && frozenTableDataRef.current ? frozenTableDataRef.current : liveTableData;

  const isWeekendRow = (row) => {
    const ts = Number(row?.spanStartMs);
    if (Number.isFinite(ts) && ts > 0) {
      try {
        const weekday = new Intl.DateTimeFormat('en-US', {
          timeZone,
          weekday: 'short'
        }).format(new Date(ts));
        return weekday === 'Sat' || weekday === 'Sun';
      } catch {
        /* fall through */
      }
    }

    const m = moment(String(row?.dateKey || ''), [
      'YYYY-MM-DD',
      'DD/MM/YYYY',
      'MM/DD/YYYY',
      'DD-MM-YYYY',
      moment.ISO_8601
    ]);
    if (!m.isValid()) return false;
    const d = m.day();
    return d === 0 || d === 6;
  };

  const columnT = [
    // {
    //   name: "DATE",
    //   selector: (row) => new Date(row?.timelogs?.createdAt).toLocaleDateString("en-GB"),
    //   sortable: false,
    //   width: "120px",
    // },
    {
      name: 'PROJECT NAME',
      selector: (row) =>
        row?.isBreakRow
          ? '-'
          : row?.isEditRequestRow
            ? row?.projectName || row?.project?.name || '-'
            : row?.project?.name || '-',
      sortable: false
    },
    {
      name: 'TASK NUMBER',
      selector: (row) =>
        row?.isBreakRow ? '-' : row?.isEditRequestRow ? 'REQ' : row?.taskKey || '-',
      sortable: false
    },
    {
      name: 'TASK NAME',
      selector: (row) => row?.title || '-',
      cell: (row) => {
        const reason = String(row?.timelogs?.reason || row?.reason || '').trim();
        const isEdited = Boolean(reason) && !row?.isBreakRow && !row?.isEditRequestRow;
        const status = String(row?.editRequestStatus || '').toLowerCase();

        return (
          <div className="att-tt-task-name-cell">
            {row?.isBreakRow ? (
              <span className="att-tt-chip att-tt-chip--break">
                <span className="att-tt-chip__icon" aria-hidden>
                  <i className="icofont-coffee-cup" />
                </span>
                <span className="att-tt-chip__label">Break</span>
              </span>
            ) : row?.isEditRequestRow ? (
              <>
                <span className="att-tt-task-name-cell__title" title={row?.title || ''}>
                  {row?.title || '-'}
                </span>
                <span
                  className={`att-tt-chip att-tt-chip--req att-tt-chip--req-${status || 'pending'}`}
                  title={reason || row?.editRequestStatus}
                >
                  <span className="att-tt-chip__label">{row?.editRequestStatus || 'Pending'}</span>
                </span>
              </>
            ) : (
              <span className="att-tt-task-name-cell__title" title={row?.title || ''}>
                {row?.title || '-'}
              </span>
            )}

            {isEdited ? (
              <OverlayTrigger
                placement="top"
                delay={{ show: 120, hide: 80 }}
                trigger={['hover', 'focus']}
                overlay={
                  <Tooltip
                    id={`att-tt-reason-${row?.timelogs?._id || row?._id}`}
                    className="att-tt-reason-tooltip"
                  >
                    <span className="att-tt-reason-tooltip__label">Edit reason</span>
                    <span className="att-tt-reason-tooltip__text">{reason}</span>
                  </Tooltip>
                }
              >
                <button
                  type="button"
                  className="att-tt-chip att-tt-chip--edited"
                  aria-label={`Edited: ${reason}`}
                >
                  <span className="att-tt-chip__icon" aria-hidden>
                    <i className="icofont-ui-edit" />
                  </span>
                  <span className="att-tt-chip__label">Edited</span>
                  <span className="att-tt-chip__info" aria-hidden>
                    <i className="icofont-info-circle" />
                  </span>
                </button>
              </OverlayTrigger>
            ) : null}

            {row?.isEditRequestRow && reason ? (
              <OverlayTrigger
                placement="top"
                delay={{ show: 120, hide: 80 }}
                trigger={['hover', 'focus']}
                overlay={
                  <Tooltip
                    id={`att-tt-req-reason-${row?.editRequestId || row?._id}`}
                    className="att-tt-reason-tooltip"
                  >
                    <span className="att-tt-reason-tooltip__label">Request reason</span>
                    <span className="att-tt-reason-tooltip__text">{reason}</span>
                  </Tooltip>
                }
              >
                <button
                  type="button"
                  className="att-tt-chip att-tt-chip--edited"
                  aria-label={`Reason: ${reason}`}
                >
                  <span className="att-tt-chip__info" aria-hidden>
                    <i className="icofont-info-circle" />
                  </span>
                </button>
              </OverlayTrigger>
            ) : null}
          </div>
        );
      },
      sortable: false
    },
    {
      name: 'START TIME',
      selector: (row) =>
        row.timelogs?.start ? formatTimedoctorTime(row.timelogs.start, timeZone) : '-',
      sortable: false,
      width: '130px'
    },
    {
      name: 'END TIME',
      selector: (row) => {
        if ((row?.isBreakRow || row?.isEditRequestRow) && !row.timelogs?.end) return '-';
        if (!row?.isBreakRow && !row?.isEditRequestRow && !row.timelogs?.end) return 'Running';
        return formatTimedoctorTime(row.timelogs.end, timeZone);
      },
      sortable: false,
      width: '130px',
      cell: (row) =>
        row?.isBreakRow || row?.isEditRequestRow ? (
          <span>{row?.timelogs?.end ? formatTimedoctorTime(row.timelogs.end, timeZone) : '-'}</span>
        ) : row.timelogs?.end ? (
          <span>{formatTimedoctorTime(row.timelogs.end, timeZone)}</span>
        ) : (
          <RunningPill />
        )
    },
    {
      name: 'DURATION',
      selector: (row) => row?.durationFormatted || '-',
      cell: (row) =>
        row?.isBreakRow ? (
          <span className="text-warning fw-semibold">{row?.durationFormatted || '-'}</span>
        ) : row?.isEditRequestRow ? (
          <span className="att-tt-req-duration">{row?.durationFormatted || '-'}</span>
        ) : (
          row?.durationFormatted || '-'
        ),
      sortable: false,
      width: '120px'
    }
    // {
    //   name: "WORKSPACES",
    //   cell: (row) =>
    //     row?.workspaceInfo ? (
    //       <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    //         <img
    //           src={row?.workspaceInfo?.logo}
    //           alt="logo"
    //           style={{
    //             width: 36,
    //             height: 36,
    //             borderRadius: "50%",
    //             border: "1px solid #ccc",
    //           }}
    //         />
    //         <span>{row?.workspaceInfo?.companyName}</span>
    //       </div>
    //     ) : (
    //       "-"
    //     ),
    //   sortable: false,
    //   width: "250px",
    // },
    // {
    //   name: "ACTION",
    //   cell: (row) =>
    //     isEditable ? (
    //       <div className="btn-group" role="group">
    //         <button
    //           className="btn btn-outline-secondary"
    //           title="Edit"
    //           onClick={() =>
    //             setState({
    //               ...state,
    //               editTime: { isModal: true, rowData: row },
    //             })
    //           }
    //         >
    //           <i className="icofont-edit text-success"></i>
    //         </button>
    //         <button
    //           className="btn btn-outline-secondary"
    //           disabled={isDeleting}
    //           onClick={() => handleDeleteTimeLog(row.timelogs._id)}
    //         >
    //           <i className="icofont-ui-delete text-danger"></i>
    //         </button>
    //       </div>
    //     ) : (
    //       <span className="text-muted">-</span>
    //     ),
    //   sortable: false,
    //   width: "150px",
    // },
  ];

  if (isEditable || isInContext) {
    columnT.push({
      name: 'ACTION',
      cell: (row) => {
        if (row?.isEditRequestRow) {
          if (!isAdminOrHr) {
            return <span className="text-muted small">{row?.editRequestStatus || 'Pending'}</span>;
          }
          const status = String(row?.editRequestStatus || '').toLowerCase();
          const canApprove = status === 'pending' || status === 'rejected';
          const canReject = status === 'pending' || status === 'approved';
          if (!canApprove && !canReject) {
            return <span className="text-muted small">{row?.editRequestStatus || '—'}</span>;
          }
          const actions = [];
          if (canApprove) {
            actions.push({
              label: 'Approve',
              icon: 'icofont-check-circled',
              variant: 'success',
              onClick: () => openEditRequestReview(row, 'Approved')
            });
          }
          if (canReject) {
            actions.push({
              label: 'Reject',
              icon: 'icofont-close-circled',
              variant: 'danger',
              onClick: () => openEditRequestReview(row, 'Rejected')
            });
          }
          return <RowActionMenu actions={actions} />;
        }

        if (row?.isBreakRow) {
          // AU unchanged: always allow fill break
          if (!isInContext) {
            return (
              <button
                type="button"
                className="att-tt-fill-break-btn"
                title="Fill break"
                onClick={() => openFillBreakModal(row, row.dayTasks || [])}
              >
                <i className="icofont-edit" />
              </button>
            );
          }

          // IN only: request/fill only when break > 5 minutes
          const breakStart = Number(row?.timelogs?.start ?? row?.breakWindowStart);
          const breakEnd = Number(row?.timelogs?.end ?? row?.breakWindowEnd);
          const breakMs =
            Number.isFinite(breakStart) && Number.isFinite(breakEnd) && breakEnd > breakStart
              ? breakEnd - breakStart
              : 0;
          const minBreakRequestMs = 5 * 60 * 1000;
          const canRequestBreak = breakMs > minBreakRequestMs;

          if (!canRequestBreak) {
            return (
              <OverlayTrigger
                placement="left"
                delay={{ show: 120, hide: 80 }}
                trigger={['hover', 'focus']}
                overlay={
                  <Tooltip id={`break-min-${row?._id || breakStart}`}>
                    Break must be more than 5 minutes to request time.
                  </Tooltip>
                }
              >
                <span
                  className="att-tt-fill-break-btn att-tt-fill-break-btn--disabled"
                  role="img"
                  aria-label="Break must be more than 5 minutes to request time"
                  tabIndex={0}
                >
                  <i className="icofont-info-circle" />
                </span>
              </OverlayTrigger>
            );
          }

          // IN Admin/HR: direct fill; IN employee: Time Edit Request
          if (!isAdminOrHr) {
            return (
              <button
                type="button"
                className="att-tt-fill-break-btn"
                title="Request time for break"
                onClick={() => {
                  setTimeEditRequestPrefill({
                    date:
                      toLocalDateKey(breakStart, timeZone) ||
                      row?.dateKey ||
                      moment().format('YYYY-MM-DD'),
                    startTime: formatClockInput(breakStart),
                    endTime: formatClockInput(breakEnd),
                    lockToBreak: true
                  });
                  setIsTimeEditRequestOpen(true);
                }}
              >
                <i className="icofont-edit" />
              </button>
            );
          }

          return (
            <button
              type="button"
              className="att-tt-fill-break-btn"
              title="Fill break"
              onClick={() => openFillBreakModal(row, row.dayTasks || [])}
            >
              <i className="icofont-edit" />
            </button>
          );
        }

        if (!isEditable) {
          return <span className="text-muted">-</span>;
        }

        return (
          <RowActionMenu
            actions={[
              {
                label: 'Edit',
                icon: 'icofont-edit',
                variant: 'success',
                onClick: () =>
                  setState({
                    ...state,
                    editTime: { isModal: true, rowData: row }
                  })
              },
              {
                label: 'Delete',
                icon: 'icofont-ui-delete',
                variant: 'danger',
                disabled: isDeleting,
                onClick: () => openDeleteTimeLogModal(row.timelogs._id)
              }
            ]}
          />
        );
      },
      width: '90px'
    });
  }

  const RunningPill = () => (
    <span className="att-tt-running" title="Running">
      <span className="att-tt-running__dot" aria-hidden />
      Running
    </span>
  );

  const hasRunningTask = (tasks) => {
    const src = (Array.isArray(tasks) ? tasks : []).filter(
      (t) => !t?.isBreakRow && !t?.isEditRequestRow
    );
    return src.some((t) => t?.timelogs?.start && !t?.timelogs?.end);
  };

  const TableSkeleton = ({ rows = 6 }) => (
    <div className="att-tt-skeleton" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={`sk-${i}`} className="att-tt-skeleton__row">
          <div className="att-tt-skeleton__cell att-tt-skeleton__cell--expander" />
          <div className="att-tt-skeleton__cell att-tt-skeleton__cell--date" />
          <div className="att-tt-skeleton__cell att-tt-skeleton__cell--timeline" />
          <div className="att-tt-skeleton__cell att-tt-skeleton__cell--time" />
          <div className="att-tt-skeleton__cell att-tt-skeleton__cell--time" />
          <div className="att-tt-skeleton__cell att-tt-skeleton__cell--total" />
          <div className="att-tt-skeleton__cell att-tt-skeleton__cell--ghost" />
          <div className="att-tt-skeleton__cell att-tt-skeleton__cell--ghost" />
          <div className="att-tt-skeleton__cell att-tt-skeleton__cell--ghost" />
        </div>
      ))}
    </div>
  );

  const columnTRef = useRef(columnT);
  columnTRef.current = columnT;

  const ExpandedComponent = useCallback(
    ({ data }) => (
      <div className="att-tt-expand">
        <div className="att-tt-expand__inner">
          <DataTable
            columns={columnTRef.current}
            data={data.rows}
            dense
            highlightOnHover={false}
            striped={false}
            noHeader
            customStyles={trackingTableStyles}
          />
        </div>
      </div>
    ),
    []
  );

  const dayTableColumns = useMemo(
    () => [
      {
        name: 'DATE',
        selector: (row) => row.dateKey,
        sortable: true,
        cell: (row) => (
          <span
            className={
              isWeekendRow(row)
                ? 'att-tt-date-badge att-tt-date-badge--weekend'
                : 'att-tt-date-badge'
            }
          >
            {row.dateKey}
          </span>
        ),
        width: '148px'
      },
      {
        name: 'TIMELINE',
        selector: (row) => row.spanStartMs || 0,
        sortable: false,
        cell: (row) => <TimelineBar row={row} />,
        width: '300px',
        minWidth: '300px',
        grow: 0
      },
      {
        name: 'START TIME',
        selector: (row) => row.startTime,
        cell: (row) => <span className="att-tt-time">{row.startTime}</span>,
        minWidth: '120px'
      },
      {
        name: 'END TIME',
        selector: (row) => (hasRunningTask(row?.rows) ? 'Running' : row.endTime),
        cell: (row) =>
          hasRunningTask(row?.rows) ? (
            <RunningPill />
          ) : (
            <span className="att-tt-time">{row.endTime}</span>
          ),
        minWidth: '120px'
      },
      {
        name: 'TOTAL TIME',
        selector: (row) => row.totalFormatted,
        cell: (row) => <span className="att-tt-time att-tt-time--total">{row.totalFormatted}</span>,
        minWidth: '120px'
      }
    ],
    [timeZone]
  );

  const dayTableElement = useMemo(
    () => (
      <div className="att-tt-table">
        <DataTable
          columns={dayTableColumns}
          data={tableData}
          expandableRows
          expandableRowsComponent={ExpandedComponent}
          highlightOnHover
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[5, 10, 20, 50]}
          progressPending={isLogsLoading || isLogsFetching}
          progressComponent={<TableSkeleton />}
          noDataComponent={
            <div className="att-tt-empty">
              <span className="att-tt-empty__icon" aria-hidden>
                <i className="bi bi-clock-history" />
              </span>
              <p className="att-tt-empty__title">No time logs in this period</p>
              <p className="att-tt-empty__text">Try another day, week, or employee.</p>
            </div>
          }
          className="table myDataTable table-hover align-middle mb-0 d-row nowrap dataTable no-footer dtr-inline"
          customStyles={trackingTableStyles}
        />
      </div>
    ),
    [dayTableColumns, tableData, ExpandedComponent, isLogsLoading, isLogsFetching]
  );

  // While Fill/Edit/Request modal is open, keep last table element so selects don't re-animate rows
  const frozenDayTableRef = useRef(null);
  if (!isModalOpen) {
    frozenDayTableRef.current = dayTableElement;
  }
  const dayTableToRender =
    isModalOpen && frozenDayTableRef.current ? frozenDayTableRef.current : dayTableElement;

  const handleDateSelection = (type) => {
    const value = moment();
    const range = buildTimedoctorRange(value, timeZone, type);
    if (!range?.from || !range?.to) return;

    setState({ ...state, selectType: { type: type, currentDate: value } });
    setFromDate(range.from);
    setToDate(range.to);
  };

  const handleDateSelectionRange = (value) => {
    const range = buildTimedoctorRange(value, timeZone, state.selectType.type);
    if (!range?.from || !range?.to) return;

    setFromDate(range.from);
    setToDate(range.to);
  };

  // Recalc API from/to when office (timezone) changes — keep selected period type/date
  useEffect(() => {
    const value = state.selectType?.currentDate || moment();
    const type = state.selectType?.type || 'Day';
    const range = buildTimedoctorRange(value, timeZone, type);
    if (!range?.from || !range?.to) return;
    setFromDate(range.from);
    setToDate(range.to);
  }, [timeZone, state.selectType?.currentDate, state.selectType?.type]);

  // RTK refetch throws if the query was never started; never let that crash the page
  const safeRefetchEmployees = () => {
    try {
      employeeDropDownQuery?.();
    } catch (err) {
      console.error('Employee dropdown refetch failed:', err);
    }
  };

  /** Returns true when the next page was requested. */
  const handleMenuScrollToBottom = () => {
    const pageRows = Array.isArray(dropdownData?.data) ? dropdownData.data.length : 0;
    // A full page also counts as "more", in case isNext is missing or wrong
    const hasMore = Boolean(dropdownData?.isNext) || pageRows >= state.rowsPerPageDropdown;
    if (!hasMore || isEmployeeDropdownFetching) return false;
    setState((prevState) => ({
      ...prevState,
      currentPageDropdown: prevState.currentPageDropdown + 1
    }));
    return true;
  };

  // react-select's onMenuScrollToBottom only reacts to wheel/touch, so also listen to the
  // list's native scroll (scrollbar drag, keyboard). The ref keeps the memoized component fresh.
  const scrollToBottomRef = useRef(handleMenuScrollToBottom);
  scrollToBottomRef.current = handleMenuScrollToBottom;
  const employeeMenuListComponents = useMemo(() => {
    let armed = true;
    const EmployeeMenuList = (props) => (
      <selectComponents.MenuList
        {...props}
        innerProps={{
          ...props.innerProps,
          onScroll: (e) => {
            const el = e?.currentTarget;
            if (!el) return;
            const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
            if (!nearBottom) {
              armed = true;
            } else if (armed) {
              armed = !scrollToBottomRef.current?.();
            }
          }
        }}
      />
    );
    return { MenuList: EmployeeMenuList };
  }, []);

  // Refetch after the page/search arg has updated so every scroll hits the API
  const dropdownFirstRunRef = useRef(true);
  useEffect(() => {
    if (dropdownFirstRunRef.current) {
      dropdownFirstRunRef.current = false;
      return;
    }
    if (canSelectEmployee) safeRefetchEmployees();
  }, [state.currentPageDropdown, state.dropdownSearch]);

  // useEffect(() => {
  //   setState({ ...state, dropdownDataStore: dropdownData?.data || [] });
  // }, []);

  useEffect(() => {
    if (dropdownData && Array.isArray(dropdownData.data)) {
      setState((prevState) => {
        // Page 1 is a fresh list (new office/search): replace, keeping only the selected employee
        const base =
          prevState.currentPageDropdown === 1
            ? prevState.dropdownDataStore.filter(
                (e) => prevState.userId && String(e._id) === String(prevState.userId)
              )
            : prevState.dropdownDataStore;
        const existing = new Set(base.map((e) => String(e._id)));
        const fresh = dropdownData.data.filter((e) => !existing.has(String(e._id)));
        return { ...prevState, dropdownDataStore: [...base, ...fresh] };
      });
    }
  }, [dropdownData]);

  // The query cache key has no locality, so reload the list when the office/locality changes
  const localityKey = `${JSON.stringify(userLocality)}|${JSON.stringify(officeLocality)}`;
  const localityFirstRunRef = useRef(true);
  useEffect(() => {
    if (localityFirstRunRef.current) {
      localityFirstRunRef.current = false;
      return;
    }
    setState((prev) => ({
      ...prev,
      userId: null,
      dropdownSearch: '',
      currentPageDropdown: 1,
      dropdownDataStore: []
    }));
    if (canSelectEmployee) safeRefetchEmployees();
  }, [localityKey]);

  // Employee search is server-side: debounce typing, then reset the paged list
  useEffect(
    () => () => {
      if (employeeSearchTimerRef.current) clearTimeout(employeeSearchTimerRef.current);
    },
    []
  );

  const handleEmployeeSearchInput = (text, meta) => {
    const action = meta?.action;
    if (employeeSearchTimerRef.current) clearTimeout(employeeSearchTimerRef.current);
    if (action !== 'input-change') {
      // Selected / menu closed / blurred: drop the search so the full list comes back.
      // The store is left alone; the page-1 response replaces it (keeping the selection).
      setState((prev) =>
        prev.dropdownSearch ? { ...prev, dropdownSearch: '', currentPageDropdown: 1 } : prev
      );
      return;
    }
    employeeSearchTimerRef.current = setTimeout(() => {
      const term = String(text || '').trim();
      setState((prev) => {
        if (prev.dropdownSearch === term) return prev;
        return {
          ...prev,
          dropdownSearch: term,
          currentPageDropdown: 1,
          // keep the selected employee so its label survives the reset
          dropdownDataStore: prev.dropdownDataStore.filter(
            (e) => prev.userId && String(e._id) === String(prev.userId)
          )
        };
      });
    }, 400);
  };

  const handleSaveTime = async () => {
    const row = state.editTime.rowData;

    const getTimestamp = (timeString) => {
      if (!timeString || !selectedDate) return null;
      // selectedDate is YYYY-MM-DD in office TZ — noon UTC keeps the same calendar day for IN/AU
      const anchorTs = Date.parse(`${selectedDate}T12:00:00.000Z`);
      if (!Number.isFinite(anchorTs)) return null;
      return wallTimeOnAnchorDayToUtcMs(timeString, anchorTs, timeZone);
    };

    const originalStart = row.timelogs.start;
    const originalEnd = row.timelogs.end;
    const originalReason = String(row.timelogs?.reason || '').trim();

    const trimmedReason = (editReasonRef.current?.value || '').trim();
    if (!trimmedReason) {
      setReasonError('Reason is required');
      return;
    }
    if (trimmedReason.length > 500) {
      setReasonError('Reason must be less than 500 characters');
      return;
    }

    const startMins = toMinutesOfDay(startTime);
    const endMins = toMinutesOfDay(endTime);
    const boundLo = toMinutesOfDay(editBoundMin) ?? 0;
    const boundHi = toMinutesOfDay(editBoundMax) ?? 23 * 60 + 59;
    if (startMins != null && (startMins < boundLo || startMins > boundHi)) {
      notification(
        `Start time must be between ${formatHhmmTo12h(editBoundMin)} and ${formatHhmmTo12h(editBoundMax)}.`,
        TOAST_ERROR
      );
      return;
    }
    if (endMins != null && (endMins < boundLo || endMins > boundHi)) {
      notification(
        `End time must be between ${formatHhmmTo12h(editBoundMin)} and ${formatHhmmTo12h(editBoundMax)} (next log start).`,
        TOAST_ERROR
      );
      return;
    }
    if (startMins != null && endMins != null && endMins < startMins) {
      notification('End time must be after start time.', TOAST_ERROR);
      return;
    }

    const startChanged = Boolean(startTime && startTime !== formatClockInput(originalStart));
    const endChanged = Boolean(
      endTime && (!originalEnd || endTime !== formatClockInput(originalEnd))
    );
    const reasonChanged = trimmedReason !== originalReason;

    const originalTaskId =
      row.taskId || row.task?._id || row.task?.id || row.timelogs?.taskId || null;
    const nextTaskId = selectedBreakTask?.value || null;
    const originalProjectId = getExplicitProjectId(row);
    const nextProjectId = selectedBreakProject?.value || null;
    const taskChanged =
      isAuContext && nextTaskId && String(nextTaskId) !== String(originalTaskId || '');
    const projectChanged =
      isAuContext && nextProjectId && String(nextProjectId) !== String(originalProjectId || '');

    if (!startChanged && !endChanged && !reasonChanged && !taskChanged && !projectChanged) {
      notification('No changes to save.', TOAST_SUCCESS);
      return closeEditModal();
    }

    if (isAuContext) {
      if (!selectedBreakProject?.value) {
        setBreakProjectError('Please select a project');
        return;
      }
      if (!selectedBreakTask?.value) {
        setBreakTaskError('Please select a task');
        return;
      }
      const sameOriginalPair =
        String(nextTaskId || '') === String(originalTaskId || '') &&
        String(nextProjectId || '') === String(originalProjectId || '');
      if (
        projectChanged &&
        !sameOriginalPair &&
        !taskBelongsToProject(
          selectedBreakTask.task || selectedBreakTask,
          selectedBreakProject.value
        )
      ) {
        setSelectedBreakTask(null);
        setBreakTaskError('Please select a task for this project');
        return;
      }
    }

    // Always send current form times (unchanged times stay as-is) + reason
    const startTimestamp = getTimestamp(startTime) || Number(originalStart);
    const endTimestamp = endTime
      ? getTimestamp(endTime) || (originalEnd ? Number(originalEnd) : null)
      : originalEnd
        ? Number(originalEnd)
        : null;

    const payload = {
      reason: trimmedReason
    };
    if (Number.isFinite(startTimestamp) && startTimestamp > 0) {
      payload.start = startTimestamp;
    }
    if (Number.isFinite(endTimestamp) && endTimestamp > 0) {
      payload.end = endTimestamp;
    }
    if (isAuContext && nextProjectId) {
      payload.projectId = nextProjectId;
    }
    if (isAuContext && nextTaskId) {
      payload.taskId = nextTaskId;
    }

    try {
      const res = await updateTimeLog({
        timeLogId: row.timelogs._id,
        data: payload
      }).unwrap();

      if (res?.success) {
        notification(res?.statusMessage || 'Time log updated.', TOAST_SUCCESS);

        closeEditModal();
        getTotalAndTaskLogs();
        getTotalLogs();
      } else {
        notification(res?.statusMessage || 'Update failed.', TOAST_ERROR);
      }
    } catch (err) {
      console.error('Update failed:', err);
      notification('Something went wrong while updating time log.', TOAST_ERROR);
    }
  };

  const handleSaveFillBreak = async () => {
    const row = state.fillBreak.rowData;
    if (!row?.timelogs) return;

    if (!selectedBreakProject) {
      setBreakProjectError('Please select a project');
      return;
    }

    if (!selectedBreakTask) {
      setBreakTaskError('Please select a task');
      return;
    }

    const trimmedReason = (editReasonRef.current?.value || '').trim();
    if (!trimmedReason) {
      setReasonError('Reason is required');
      return;
    }
    if (trimmedReason.length > 500) {
      setReasonError('Reason must be less than 500 characters');
      return;
    }

    const minTs = Number(row.breakWindowStart ?? row.timelogs.start);
    const maxTs = Number(row.breakWindowEnd ?? row.timelogs.end);

    const toTsOnBreakDay = (timeString, anchorTs) =>
      wallTimeOnAnchorDayToUtcMs(timeString, Number(anchorTs), timeZone);

    let startTs = toTsOnBreakDay(startTime, minTs);
    let endTs = toTsOnBreakDay(endTime, maxTs);

    if (!startTs || !endTs) {
      notification('Please select valid start and end time.', TOAST_ERROR);
      return;
    }

    // HH:mm UI drops seconds — snap to real window edges when those minutes are selected
    if (startTime === formatClockInput(minTs)) startTs = minTs;
    if (endTime === formatClockInput(maxTs)) endTs = maxTs;

    // Clamp any other pick into the break window (seconds-safe)
    startTs = Math.max(minTs, Math.min(maxTs, startTs));
    endTs = Math.max(minTs, Math.min(maxTs, endTs));

    if (startTs >= endTs) {
      notification('End time must be after start time.', TOAST_ERROR);
      return;
    }

    const selectedTask = selectedBreakTask.task || {};
    const taskId =
      selectedTask.taskId ||
      selectedTask.task?._id ||
      selectedTask.task?.id ||
      selectedTask._id ||
      selectedBreakTask.value;

    if (!taskId) {
      setBreakTaskError('Please select a valid task');
      return;
    }

    const projectId = normalizeId(selectedBreakProject?.value);
    if (
      projectId &&
      !taskBelongsToProject(selectedBreakTask.task || selectedBreakTask, projectId)
    ) {
      setSelectedBreakTask(null);
      setBreakTaskError('Please select a task for this project');
      return;
    }

    try {
      const location = await getBrowserLocation();
      const locationPayload = {
        lat: Number(location.lat) || 0,
        long: Number(location.long) || 0
      };

      // Break edit day — ISO with UTC offset, e.g. 2026-07-22T05:46:43.110+00:00
      const createdAt = toUtcOffsetIso(startTs);
      const updatedAt = toUtcOffsetIso(endTs);

      const userId =
        state?.userId && (canSelectEmployee || isAdminOrHr) ? state.userId : values?._id;

      const commonPayload = {
        taskId,
        userId,
        startTime: startTs,
        endTime: endTs,
        reason: trimmedReason,
        createdAt,
        updatedAt,
        hrxEditTime: true,
        location: locationPayload
      };

      // type: "start" API skipped — fill break only calls type: "end"
      // const startRes = await startEndTimer({
      //   taskId,
      //   data: {
      //     type: 'start',
      //     ...commonPayload
      //   }
      // }).unwrap();
      //
      // if (startRes?.success === false) {
      //   notification(startRes?.statusMessage || 'Failed to start timer.', TOAST_ERROR);
      //   return;
      // }

      const endRes = await startEndTimer({
        taskId,
        data: {
          type: 'end',
          ...commonPayload
        }
      }).unwrap();

      if (endRes?.success === false) {
        notification(endRes?.statusMessage || 'Failed to end timer.', TOAST_ERROR);
        return;
      }

      notification(
        endRes?.statusMessage ||
          (isInContext && isAdminOrHr
            ? 'Break filled — time log saved.'
            : 'Break filled successfully.'),
        TOAST_SUCCESS
      );
      closeEditModal();
      getTotalAndTaskLogs();
      getTotalLogs();
    } catch (err) {
      console.error('Fill break failed:', err);
      notification(
        err?.data?.statusMessage ||
          err?.data?.message ||
          (err?.status === 404 ? 'Timer API not found (404). Check task id / route.' : null) ||
          'Something went wrong while filling break.',
        TOAST_ERROR
      );
    }
  };

  return (
    <>
      <div className="container-xxl att-tt-page att-tt-page--tracking">
        <div className="att-tt-page__title">
          <PageHeader headerTitle={ATTENDANCE_TIME_TRACKING} />
        </div>

        <div className="att-tt-body">
          <div className={`att-tt-surface${isModalOpen ? ' att-tt-surface--modal-open' : ''}`}>
            <div className="att-tt-surface__toolbar">
              <div className="att-tt-toolbar-row">
                <div className="att-tt-toolbar-row__stats">
                  <div className="att-tt-stat">
                    <span className="att-tt-stat__icon" aria-hidden>
                      <i className="bi bi-clock-history" />
                    </span>
                    <div className="att-tt-stat__content">
                      <span className="att-tt-stat__label">{TOTAL_TIME}</span>
                      <span className="att-tt-stat__value">
                        {trackedTimeParts(totalTrackedMs).map((part) => (
                          <span key={part.key} className="att-tt-stat__part">
                            {part.value}
                            <span className="att-tt-stat__unit">{part.key}</span>
                          </span>
                        ))}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="att-tt-toolbar-row__filters">
                  {canSelectEmployee && (
                    <div className="att-tt-control att-tt-control--employee">
                      <span className="att-tt-control__icon" aria-hidden>
                        <i className="bi bi-person" />
                      </span>
                      <Select
                        classNamePrefix="att-tt-select"
                        className="att-tt-select-wrap"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        options={state.dropdownDataStore?.map((val) => ({
                          value: val._id,
                          label: val.name
                        }))}
                        value={
                          state.userId
                            ? {
                                value: state.userId,
                                label:
                                  state.dropdownDataStore?.find(
                                    (e) => String(e._id) === String(state.userId)
                                  )?.name || 'Employee'
                              }
                            : null
                        }
                        filterOption={null}
                        isLoading={isEmployeeDropdownFetching}
                        onInputChange={handleEmployeeSearchInput}
                        isClearable
                        components={employeeMenuListComponents}
                        placeholder="Select Employee..."
                        onChange={(selectedOption) => {
                          if (!selectedOption) {
                            setState((prev) => ({ ...prev, userId: null }));
                            return;
                          }
                          const selectedEmployee = state.dropdownDataStore.find(
                            (employee) => employee._id === selectedOption.value
                          );
                          if (selectedEmployee) {
                            const userId = selectedEmployee._id;
                            setState((prev) => ({
                              ...prev,
                              userId: userId,
                              loading: true
                            }));
                          }
                        }}
                      />
                    </div>
                  )}

                  <div className="att-tt-tabs" role="tablist" aria-label="Time period">
                    {['Day', 'Week', 'Month'].map((period) => (
                      <button
                        key={period}
                        type="button"
                        role="tab"
                        aria-selected={state.selectType.type === period}
                        className={`att-tt-tab${state.selectType.type === period ? ' att-tt-tab--active' : ''}`}
                        onClick={() => handleDateSelection(period)}
                      >
                        {period}
                      </button>
                    ))}
                  </div>

                  <div
                    className={`att-tt-date att-tt-date--${state.selectType.type.toLowerCase()}`}
                  >
                    <span className="att-tt-date__icon" aria-hidden>
                      <i className="bi bi-calendar3" />
                    </span>
                    <div className="att-tt-date__picker">
                      <DatePicker
                        initialDate={state.selectType.currentDate}
                        type={state.selectType.type}
                        handleChangeDate={handleDateSelectionRange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="att-tt-surface__body">{dayTableToRender}</div>
          </div>
        </div>
      </div>

      <div
        className={`offcanvas offcanvas-end`}
        tabIndex="-1"
        id="offcanvasRight"
        aria-labelledby="offcanvasRightLabel"
        style={{ width: '50%' }}
      >
        <div className="offcanvas-header">
          <h4 className="offcanvas-title" id="offcanvasRightLabel">
            All Workspaces - {msToHms(totalTrackedMs)}
          </h4>
          <button
            type="button"
            className="btn-close text-reset"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          ></button>
        </div>
        <div className="offcanvas-body">
          <div style={{ fontSize: '16px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {/* {Object.values(totalLogs?.data.data || {}).flat().map((work, i) => (
                <Card key={i} style={{ width: '47%' }} className="h-100 me-3 mb-3">
                  <Card.Body>
                    <Card.Title
                      className="fw-bold"
                      style={{
                        fontSize: 'inherit'
                      }}
                    >
                      <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        <span>
                          <img
                            src={work?.logo}
                            alt="logo"
                            style={{
                              borderRadius: '50%',
                              height: '40px',
                              width: '40px',
                              border: '1px solid #c3c3c3'
                            }}
                          />
                        </span>
                        <span>{work?.companyName}</span>
                      </div>
                    </Card.Title>
                    <p className="pt-3 fs-3 fw-medium">
                      {secondsToHms(work?.duration || 0)}
                    </p>
                  </Card.Body>
                </Card>
              ))} */}

              {totalLogs?.data?.data && (
                <Card style={{ width: '47%' }} className="h-100 me-3 mb-3">
                  <Card.Body>
                    <Card.Title className="fw-bold" style={{ fontSize: 'inherit' }}>
                      <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        <span>
                          <img
                            src={totalLogs.data.data.logo}
                            alt="logo"
                            style={{
                              borderRadius: '50%',
                              height: '40px',
                              width: '40px',
                              border: '1px solid #c3c3c3'
                            }}
                          />
                        </span>
                        <span>{totalLogs.data.data.companyName}</span>
                      </div>
                    </Card.Title>

                    <p className="pt-3 fs-3 fw-medium">{msToHms(totalTrackedMs)}</p>
                  </Card.Body>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Start Time and End Time Update Modal */}

      <Modal centered animation={false} show={state.editTime.isModal} onHide={closeEditModal}>
        <Modal.Header>
          <h5 className="modal-title" id="exampleModalLiveLabel">
            Update time
          </h5>
          <button type="button" className="btn-close" onClick={closeEditModal}></button>
        </Modal.Header>

        <Modal.Body>
          <div className="d-flex gap-4 mb-3">
            <span style={{ width: '100%' }}>
              <label className="form-label">Select Date</label>
              <input
                type="date"
                className="form-control"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                disabled
              />
            </span>
          </div>

          {isAuContext ? (
            <>
              <div className="mb-3">
                <label className="form-label">
                  Project <span className="text-danger">*</span>
                </label>
                <Select
                  classNamePrefix="att-tt-fill-select"
                  options={
                    selectedBreakProject?.value &&
                    !projectOptions.some(
                      (o) => String(o.value) === String(selectedBreakProject.value)
                    )
                      ? [selectedBreakProject, ...projectOptions]
                      : projectOptions
                  }
                  value={selectedBreakProject}
                  isDisabled={!isEditable}
                  isLoading={isProjectsLoading}
                  isSearchable
                  placeholder={isProjectsLoading ? 'Loading projects...' : 'Select project...'}
                  onChange={(opt) => {
                    editProjectTouchedRef.current = true;
                    setSelectedBreakProject(opt);
                    setSelectedBreakTask(null);
                    setBreakProjectError('');
                    setBreakTaskError('');
                  }}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 100050 }),
                    control: (base) => ({
                      ...base,
                      minHeight: 42,
                      height: 42,
                      borderRadius: 10,
                      borderColor: breakProjectError ? '#dc3545' : '#e5e7eb',
                      boxShadow: 'none',
                      alignItems: 'center'
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      height: 40,
                      padding: '0 8px',
                      alignItems: 'center',
                      flexWrap: 'nowrap'
                    }),
                    input: (base) => ({
                      ...base,
                      margin: 0,
                      padding: 0,
                      gridArea: '1 / 1 / 2 / 2'
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      height: 40,
                      alignItems: 'center'
                    }),
                    indicatorSeparator: () => ({ display: 'none' }),
                    placeholder: (base) => ({
                      ...base,
                      margin: 0,
                      lineHeight: '40px',
                      gridArea: '1 / 1 / 2 / 2'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      margin: 0,
                      lineHeight: '40px',
                      gridArea: '1 / 1 / 2 / 2'
                    })
                  }}
                />
                {breakProjectError ? (
                  <small className="text-danger d-block mt-1">{breakProjectError}</small>
                ) : null}
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Task <span className="text-danger">*</span>
                </label>
                <Select
                  classNamePrefix="att-tt-fill-select"
                  options={fillBreakTaskOptions}
                  value={selectedBreakTask}
                  isDisabled={!isEditable || !selectedBreakProject}
                  isLoading={isTasksLoading}
                  onChange={(opt) => {
                    setSelectedBreakTask(opt);
                    setBreakTaskError('');
                  }}
                  placeholder={
                    !selectedBreakProject
                      ? 'Select project first...'
                      : isTasksLoading
                        ? 'Loading tasks...'
                        : 'Select task...'
                  }
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 100050 }),
                    control: (base) => ({
                      ...base,
                      minHeight: 42,
                      height: 42,
                      borderRadius: 10,
                      borderColor: breakTaskError ? '#dc3545' : '#e5e7eb',
                      boxShadow: 'none',
                      alignItems: 'center'
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      height: 40,
                      padding: '0 8px',
                      alignItems: 'center',
                      flexWrap: 'nowrap'
                    }),
                    input: (base) => ({
                      ...base,
                      margin: 0,
                      padding: 0,
                      gridArea: '1 / 1 / 2 / 2'
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      height: 40,
                      alignItems: 'center'
                    }),
                    indicatorSeparator: () => ({ display: 'none' }),
                    placeholder: (base) => ({
                      ...base,
                      margin: 0,
                      lineHeight: '40px',
                      gridArea: '1 / 1 / 2 / 2'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      margin: 0,
                      lineHeight: '40px',
                      gridArea: '1 / 1 / 2 / 2'
                    })
                  }}
                />
                {breakTaskError ? (
                  <small className="text-danger d-block mt-1">{breakTaskError}</small>
                ) : null}
              </div>
            </>
          ) : null}

          <div className="d-flex gap-4 mb-3">
            <span style={{ width: '50%' }}>
              <label className="form-label">Start time</label>
              <BreakWindowTimePicker
                value={startTime}
                minTime={editBoundMin}
                maxTime={endTime || editBoundMax}
                onChange={(next) => {
                  setStartTime(next);
                  if (endTime && next && toMinutesOfDay(endTime) < toMinutesOfDay(next)) {
                    setEndTime(next);
                  }
                }}
                placeholder="Select start time"
                disabled={!isEditable}
              />
            </span>

            <span style={{ width: '50%' }}>
              <label className="form-label">End time</label>
              <BreakWindowTimePicker
                value={endTime}
                minTime={editBoundMin}
                maxTime={editBoundMax}
                lowerBound={startTime || editBoundMin}
                onChange={setEndTime}
                placeholder="Select end time"
                disabled={!isEditable}
              />
              {editBoundMax && editBoundMax !== '23:59' ? (
                <small className="text-muted d-block mt-1">
                  Max end: {formatHhmmTo12h(editBoundMax)} (next log start)
                </small>
              ) : null}
            </span>
          </div>

          <div>
            <label className="form-label" htmlFor="att-tt-edit-reason">
              Reason <span className="text-danger">*</span>
            </label>
            <textarea
              key={`edit-reason-${reasonFieldKey}`}
              id="att-tt-edit-reason"
              className={`form-control ${reasonError ? 'is-invalid' : ''}`}
              rows={3}
              placeholder={PLACEHOLDER.PH_SUPPORT_TICKET_REASON || 'Enter reason'}
              defaultValue={state.editTime.rowData?.timelogs?.reason || ''}
              disabled={!isEditable}
              ref={editReasonRef}
              onChange={() => {
                if (reasonError) setReasonError('');
              }}
            />
            {reasonError ? (
              <div className="invalid-feedback d-block">{reasonError}</div>
            ) : (
              <small className="text-muted">
                Required when updating start, end, project, or task.
              </small>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={closeEditModal}>
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveTime}
            disabled={isUpdating || !isEditable}
          >
            {isUpdating ? 'Saving...' : 'Save changes'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Fill Break Modal (AU + IN Admin/HR direct save) */}
      <Modal
        centered
        animation={false}
        show={state.fillBreak.isModal}
        onHide={closeEditModal}
        dialogClassName="att-tt-fill-break-modal"
      >
        <Modal.Header closeButton className="att-tt-fill-break-modal__header">
          <div>
            <h5 className="modal-title mb-1">Fill break</h5>
            <p className="att-tt-fill-break-modal__subtitle mb-0">
              {isInContext && isAdminOrHr
                ? 'Time is saved directly — no approval request'
                : 'Add work time inside this break gap'}
            </p>
          </div>
        </Modal.Header>

        <Modal.Body className="att-tt-fill-break-modal__body">
          <div className="att-tt-fill-break-modal__window" role="note">
            <span className="att-tt-fill-break-modal__window-label">Break window</span>
            <span className="att-tt-fill-break-modal__window-value">
              {breakTimeMin ? formatHhmmTo12h(breakTimeMin) : '--:--'} –{' '}
              {breakTimeMax ? formatHhmmTo12h(breakTimeMax) : '--:--'}
            </span>
            {selectedDate ? (
              <span className="att-tt-fill-break-modal__window-date">
                {moment(selectedDate, 'YYYY-MM-DD').format('DD MMM YYYY')}
              </span>
            ) : null}
          </div>

          <div className="att-tt-fill-break-modal__field">
            <label className="att-tt-fill-break-modal__label">
              Project <span className="text-danger">*</span>
            </label>
            <Select
              classNamePrefix="att-tt-fill-select"
              className="att-tt-fill-break-modal__select"
              options={projectOptions}
              value={selectedBreakProject}
              isLoading={isProjectsLoading}
              isSearchable
              placeholder="Select project..."
              onChange={(opt) => {
                fillBreakProjectTouchedRef.current = true;
                setSelectedBreakProject(opt);
                setSelectedBreakTask(null);
                setBreakProjectError('');
                setBreakTaskError('');
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 100050 }),
                control: (base) => ({
                  ...base,
                  minHeight: 42,
                  height: 42,
                  borderRadius: 10,
                  borderColor: breakProjectError ? '#dc3545' : '#e5e7eb',
                  boxShadow: 'none',
                  alignItems: 'center'
                }),
                valueContainer: (base) => ({
                  ...base,
                  height: 40,
                  padding: '0 8px',
                  alignItems: 'center'
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  height: 40
                }),
                indicatorSeparator: () => ({ display: 'none' })
              }}
            />
            {breakProjectError ? (
              <small className="text-danger d-block mt-1">{breakProjectError}</small>
            ) : null}
          </div>

          <div className="att-tt-fill-break-modal__field">
            <label className="att-tt-fill-break-modal__label">
              Task <span className="text-danger">*</span>
            </label>
            <Select
              classNamePrefix="att-tt-fill-select"
              className="att-tt-fill-break-modal__select"
              options={fillBreakTaskOptions}
              value={selectedBreakTask}
              isDisabled={!selectedBreakProject}
              isLoading={isTasksLoading}
              onChange={(opt) => {
                setSelectedBreakTask(opt);
                setBreakTaskError('');
              }}
              placeholder={
                !selectedBreakProject
                  ? 'Select project first...'
                  : isTasksLoading
                    ? 'Loading tasks...'
                    : 'Select task...'
              }
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 100050 }),
                control: (base) => ({
                  ...base,
                  minHeight: 42,
                  height: 42,
                  borderRadius: 10,
                  borderColor: '#e5e7eb',
                  boxShadow: 'none',
                  alignItems: 'center'
                }),
                valueContainer: (base) => ({
                  ...base,
                  height: 40,
                  padding: '0 8px',
                  alignItems: 'center',
                  flexWrap: 'nowrap'
                }),
                input: (base) => ({
                  ...base,
                  margin: 0,
                  padding: 0,
                  gridArea: '1 / 1 / 2 / 2'
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  height: 40,
                  alignItems: 'center'
                }),
                indicatorSeparator: () => ({ display: 'none' }),
                placeholder: (base) => ({
                  ...base,
                  margin: 0,
                  lineHeight: '40px',
                  gridArea: '1 / 1 / 2 / 2'
                }),
                singleValue: (base) => ({
                  ...base,
                  margin: 0,
                  lineHeight: '40px',
                  gridArea: '1 / 1 / 2 / 2'
                })
              }}
            />
            {breakTaskError ? (
              <small className="text-danger d-block mt-1">{breakTaskError}</small>
            ) : null}
          </div>

          <div className="att-tt-fill-break-modal__times">
            <div className="att-tt-fill-break-modal__field">
              <label className="att-tt-fill-break-modal__label">Start time</label>
              <BreakWindowTimePicker
                value={startTime}
                minTime={breakTimeMin}
                maxTime={breakTimeMax}
                onChange={(next) => {
                  if (endTime && next && next > endTime) setEndTime(next);
                  setStartTime(next);
                }}
              />
            </div>
            <div className="att-tt-fill-break-modal__field">
              <label className="att-tt-fill-break-modal__label">End time</label>
              <BreakWindowTimePicker
                value={endTime}
                minTime={breakTimeMin}
                maxTime={breakTimeMax}
                lowerBound={startTime || breakTimeMin}
                onChange={setEndTime}
              />
            </div>
          </div>

          <div className="att-tt-fill-break-modal__field">
            <label className="att-tt-fill-break-modal__label" htmlFor="att-tt-fill-break-reason">
              Reason <span className="text-danger">*</span>
            </label>
            <textarea
              key={`fill-reason-${reasonFieldKey}`}
              id="att-tt-fill-break-reason"
              className={`form-control att-tt-fill-break-modal__textarea ${reasonError ? 'is-invalid' : ''}`}
              rows={3}
              placeholder={PLACEHOLDER.PH_SUPPORT_TICKET_REASON || 'Enter reason'}
              defaultValue=""
              ref={editReasonRef}
              onChange={() => {
                if (reasonError) setReasonError('');
              }}
            />
            {reasonError ? <div className="invalid-feedback d-block">{reasonError}</div> : null}
          </div>
        </Modal.Body>

        <Modal.Footer className="att-tt-fill-break-modal__footer">
          <button
            type="button"
            className="btn btn-light att-tt-fill-break-modal__btn-cancel"
            onClick={closeEditModal}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary att-tt-fill-break-modal__btn-save"
            onClick={handleSaveFillBreak}
            disabled={isCreating}
          >
            {isCreating ? 'Saving...' : 'Save'}
          </button>
        </Modal.Footer>
      </Modal>

      {isInContext ? (
        <TimeEditRequestModal
          show={isTimeEditRequestOpen}
          onHide={() => {
            setIsTimeEditRequestOpen(false);
            setTimeEditRequestPrefill(null);
          }}
          assigneeId={selectedAssigneeId}
          timeZone={timeZone}
          defaultDate={
            timeEditRequestPrefill?.date ||
            (state.selectType.type === 'Day'
              ? toLocalDateKey(Date.parse(fromDate), timeZone) ||
                moment(fromDate).format('YYYY-MM-DD')
              : moment().format('YYYY-MM-DD'))
          }
          defaultStartTime={timeEditRequestPrefill?.startTime || ''}
          defaultEndTime={timeEditRequestPrefill?.endTime || ''}
          lockToBreak={Boolean(timeEditRequestPrefill?.lockToBreak)}
          canPickUserId={isAdminOrHr && Boolean(state?.userId)}
        />
      ) : null}

      <Modal
        centered
        animation={false}
        show={editRequestReview.open}
        onHide={closeEditRequestReview}
        className="att-tt-edit-req-review-modal"
      >
        <Modal.Header closeButton>
          <h5 className="modal-title mb-0 fw-bold">
            {editRequestReview.status === 'Approved' ? 'Approve request' : 'Reject request'}
          </h5>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            {editRequestReview.status === 'Approved'
              ? 'On approve, a new PMS time log is created for this request.'
              : 'Rejected requests do not create a time log.'}
          </p>
          <label className="form-label fw-semibold">Review note (optional)</label>
          <textarea
            className="form-control"
            rows={3}
            maxLength={500}
            value={editRequestReview.note}
            onChange={(e) => setEditRequestReview((prev) => ({ ...prev, note: e.target.value }))}
            placeholder="Add a note for the employee..."
          />
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={closeEditRequestReview}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn ${
              editRequestReview.status === 'Approved' ? 'btn-success' : 'btn-danger'
            }`}
            disabled={isReviewingEditRequest}
            onClick={handleEditRequestReviewSave}
          >
            {isReviewingEditRequest
              ? 'Saving...'
              : editRequestReview.status === 'Approved'
                ? 'Approve'
                : 'Reject'}
          </button>
        </Modal.Footer>
      </Modal>

      <ConfirmDialog
        show={state.deleteTimeLog.isModal}
        onHide={closeDeleteTimeLogModal}
        onConfirm={handleDeleteTimeLog}
        loading={isDeleting}
        variant="danger"
        icon="icofont-ui-delete"
        title="Delete time log?"
        message="Are you sure you want to delete this time log? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </>
  );
}

export default AttendanceTimeTracking;
